import React, { useCallback, useState, useEffect, useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowLeft, BadgeCheck, Check, CheckCircle2, Clock3, Edit, Save, Wrench, X, XCircle, MessageSquare, ChevronUp, ChevronDown, Plus, Clock, Search, Archive, Mail, DollarSign, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import type { MaintenanceListNavigationState } from './MaintenanceList';
import { Asset, MaintenanceItem, MaintenanceRequest, MaintenanceRegistration, AttachmentInfo, MaintenanceStatusEntry, MaintenanceUpdate, MaintenanceRequestResponse, MaintenanceFilters } from '../types/maintenance';
import { apiService, maintenanceCacheEvents, type MaintenanceCacheInvalidationReason } from '../services/api';
import { TransferResponsibilityModal, ApprovalModal, RejectionModal, CompletionModal, AttachmentDeletionModal, UpdateModal, CFOValueConfirmModal, TriageModal, SendQuotationsModal, AddQuotationModal, ApproveQuotationModal, SubmitForApprovalModal, StatusHistoryPanel } from '../components/maintenance';
import PartnerInfoPanel from '../components/shared/PartnerInfoPanel';
import SearchableSelect from '../components/shared/SearchableSelect';
import StyledSelect from '../components/shared/StyledSelect';
import { Header, AssetStatusBadge, WarrantyBadge } from '../components/shared';
import toast from 'react-hot-toast';

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { computeQuotationsFulfilled, AUTO_APPROVE_PURCHASE_THRESHOLD } from '../utils/quotations';
import { getStatusLabel, getApprovedDatePrefix, expandStatusFilter } from '../utils/status';

const STATUS_BADGE_MAP: Record<string, string> = {
  AGUARDANDO_TRIAGEM: 'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200',
  PENDENTE: 'bg-brand-warning-50 text-brand-warning-700 ring-1 ring-inset ring-brand-warning-200',
  APROVADO: 'bg-brand-primary-50 text-brand-primary-700 ring-1 ring-inset ring-brand-primary-200',
  EXECUTADO: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',
  REJEITADO: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  CONCLUIDO: 'bg-brand-accent-50 text-brand-accent-700 ring-1 ring-inset ring-brand-accent-200',
  ARQUIVADO: 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-200',
  // Legado para compatibilidade
  pendente: 'bg-brand-warning-50 text-brand-warning-700 ring-1 ring-inset ring-brand-warning-200',
  aprovado: 'bg-brand-primary-50 text-brand-primary-700 ring-1 ring-inset ring-brand-primary-200',
  rejeitado: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  concluido: 'bg-brand-accent-50 text-brand-accent-700 ring-1 ring-inset ring-brand-accent-200',
};

const STATUS_ICON_MAP: Record<string, LucideIcon> = {
  AGUARDANDO_TRIAGEM: Search,
  PENDENTE: Clock3,
  APROVADO: CheckCircle2,
  EXECUTADO: Wrench,
  REJEITADO: XCircle,
  CONCLUIDO: BadgeCheck,
  ARQUIVADO: Archive,
  // Legado para compatibilidade
  pendente: Clock3,
  aprovado: CheckCircle2,
  rejeitado: XCircle,
  concluido: BadgeCheck,
};

const FALLBACK_STATUS_BADGE_CLASS =
  'bg-brand-neutral-100 text-brand-neutral-600 ring-1 ring-inset ring-brand-neutral-200';

interface FormData {
  equipmentId: string;
  problemDescription: string;
  urgency: 'baixa' | 'media' | 'alta' | 'critica';
  estimatedCost?: number | string;
  actualCost?: number | string;
  solutionDescription?: string;
  responsibleTechnician?: string;
  rating?: number;
  finalObservations?: string;
  attachments?: File[];
  attachmentUrls?: AttachmentInfo[];
  
  // Novos campos conforme specs/004-novas-regras-ao
  /** Data prevista para agendamento (necessário para mover para PENDENTE) */
  scheduledArrangementDate?: Date;
  /** Observações da execução (mínimo 10 caracteres) */
  executionNotes?: string;
  /** Data e hora da execução física (usado para conclusão e status EXECUTADO) */
  executedAt?: Date;
  /** ID do parceiro aprovado */
  partnerId?: string;
  /** Observações do parceiro (read-only, carregado do cadastro) */
  partnerNotes?: string;
  
  // Campos de recorrência (feature 005-add-recurring-maintenance)
  /** Sugestão de recorrência carregada da API */
  recurrenceSuggestion?: {
    recurrence: {
      quantity: number;
      unit: 'd' | 's' | 'm';
      lastExecutionDate?: string;
      nextDueDate?: string;
      recurrenceString: string;
      inactive: boolean;
    };
    hasConflict: boolean;
    conflictMessage?: string;
  };
  /** Motivo da sobreposição manual quando usuário ignora sugestão */
  overrideReason?: string;

  // Novos campos para triagem e subcategorias
  /** Tipo de registo: 'requisicao' (Situação por Resolver) ou 'registo' (Registo de Execução) */
  type?: 'requisicao' | 'registo';
  /** Subcategoria: 'manutencao' ou 'compras' (apenas para tipo='requisicao') */
  subcategory?: 'manutencao' | 'compras' | null;
}

interface FormMode {
  type: 'request' | 'registration';
  requestId?: string;
  isEdit?: boolean;
}

/**
 * Payload de navegação entre requisições via setas (issue #208).
 *
 * Transportado via `react-router-dom` `location.state` quando o utilizador
 * abre uma requisição a partir da lista. Permite ao formulário saber quais
 * são as requisições "anterior" e "seguinte" dentro do mesmo conjunto
 * filtrado, sem ter de refazer a query à API.
 *
 *  - `filters`: filtros aplicados na lista-mãe. Usado para fallback quando
 *    a lista de IDs visíveis é parcial (paginada) — permite pedir ao backend
 *    a página completa do conjunto filtrado.
 *  - `visibleIds`: IDs presentes na lista no momento da navegação (ordenados
 *    pela mesma ordem visual da tabela).
 *  - `currentIndex`: índice do ID actual dentro de `visibleIds` (-1 se não
 *    encontrado).
 *  - `currentPage` / `pageSize`: paginação original para fallback.
 */
export interface NavigationContextPayload {
  filters?: MaintenanceFilters;
  visibleIds: string[];
  currentIndex: number;
  currentPage?: number;
  pageSize?: number;
}

export default function MaintenanceForm() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [mode, setMode] = useState<FormMode>({ type: 'request' });
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentRequest, setCurrentRequest] = useState<MaintenanceItem | null>(null);
  
  // Modal states for approval/rejection
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [, setIsMarkingExecuted] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  
  // ESTADOS REMOVIDOS - Não são mais necessários com a nova abordagem
  /*
  // Modal states for attachment removal
  const [attachmentToRemove, setAttachmentToRemove] = useState<{
    id?: string;
    name: string;
    index: number;
    isExistingFile: boolean;
  } | null>(null);
  const [isRemovingAttachment, setIsRemovingAttachment] = useState(false);
  const [attachmentRemovalError, setAttachmentRemovalError] = useState<string | null>(null);
  */
  
  // Estados para controle de exclusões pendentes
  const [pendingDeletions, setPendingDeletions] = useState<{
    id: string;
    name: string;
    originalIndex: number;
    webViewLink: string;
    size?: number;
  }[]>([]);

  const [showPendingDeletionsModal, setShowPendingDeletionsModal] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    equipmentId: '',
    problemDescription: '',
    urgency: 'media',
    estimatedCost: undefined,
    actualCost: undefined,
    solutionDescription: '',
    responsibleTechnician: '',
    rating: undefined,
    finalObservations: '',
    attachments: [],
    // Novos campos para triagem e subcategorias
    type: 'requisicao',
    subcategory: null,
    attachmentUrls: [],
    
    // Novos campos inicializados
    scheduledArrangementDate: undefined,
    executionNotes: '',
    executedAt: undefined,
    partnerId: undefined,
    partnerNotes: '',
    
    // Campos de recorrência inicializados
    recurrenceSuggestion: undefined,
    overrideReason: '',
  });

  // Estados para controle de recorrência
  const [loadingRecurrence, setLoadingRecurrence] = useState(false);
  const [recurrenceError, setRecurrenceError] = useState<string | null>(null);

  // Estados para upload de arquivos
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [attachmentPreviews, setAttachmentPreviews] = useState<{ file?: File, preview: string, name: string, webViewLink?: string }[]>([]);

  // Updates states
  const [showUpdates, setShowUpdates] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [showCFOValueConfirmModal, setShowCFOValueConfirmModal] = useState(false);
  const [showTriageModal, setShowTriageModal] = useState(false);
  const [showSendQuotationsModal, setShowSendQuotationsModal] = useState(false);
  const [showAddQuotationModal, setShowAddQuotationModal] = useState(false);
  const [showApproveQuotationModal, setShowApproveQuotationModal] = useState(false);
  const [showSubmitForApprovalModal, setShowSubmitForApprovalModal] = useState(false);
  const [quotationActionLoading, setQuotationActionLoading] = useState(false);

  // Issue #208 — navegação direta por setas entre requisições dentro do
  // filtro activo. Quando o utilizador abre o detalhe de uma requisição
  // a partir da lista, esta state contém:
  //   - os filtros aplicados (para re-popular a lista no form);
  //   - os IDs visíveis na lista, na mesma ordem;
  //   - o índice da requisição actualmente aberta.
  // As setas "<" / ">" usam esta informação para navegar.
  const [siblingNavigation, setSiblingNavigation] = useState<{
    visibleIds: string[];
    currentIndex: number;
  } | null>(null);
  const [siblingNavigationLoading, setSiblingNavigationLoading] = useState(false);

  const locationState = location.state as MaintenanceListNavigationState | null;
  const navigationContext = locationState?.navigationContext;

  // Serializa o navigationContext para uma chave estável para usar como
  // dependência de effect sem causar re-renders infinitos (location.state
  // é um objecto novo em cada render).
  const navigationContextKey = useMemo(() => {
    if (!navigationContext) return ''
    return JSON.stringify({
      id,
      visibleIds: navigationContext.visibleIds,
      currentIndex: navigationContext.currentIndex,
    })
  }, [id, navigationContext])

  const getStatusBadgeClass = useCallback(
    (status?: MaintenanceItem['status'] | null) =>
      status ? STATUS_BADGE_MAP[status] ?? FALLBACK_STATUS_BADGE_CLASS : FALLBACK_STATUS_BADGE_CLASS,
    [],
  );

  const getStatusIcon = useCallback(
    (status?: MaintenanceItem['status'] | null) =>
      status ? STATUS_ICON_MAP[status] ?? null : null,
    [],
  );

  const invalidateMaintenanceCache = useCallback(
    (reason: MaintenanceCacheInvalidationReason, requestId?: string, storeId?: string) => {
      maintenanceCacheEvents.emit({
        reason,
        requestId,
        storeId: storeId?.toLowerCase(),
        timestamp: Date.now()
      });
    },
    []
  );

  const canPostUpdate = useMemo(() => {
    // Todos os utilizadores podem adicionar updates em qualquer requisição, independente do status
    return !!(currentRequest && user);
  }, [currentRequest, user]);

  const handleUpdateSuccess = useCallback((updatedRequest: MaintenanceItem) => {
    // Validar se o updatedRequest é válido
    if (!updatedRequest?.id) {
      toast.error('Erro ao processar atualização: dados inválidos');
      return;
    }
    
    // Atualizar estado local
    setCurrentRequest(updatedRequest);
    
    // Invalidar cache com fallback para storeId
    const storeId = updatedRequest.storeId || currentRequest?.storeId || user?.store;
    invalidateMaintenanceCache('update', updatedRequest.id, storeId);
    
    // Auto-expandir seção de updates
    setShowUpdates(true);
    
    toast.success('Update adicionado com sucesso!');
  }, [invalidateMaintenanceCache, currentRequest?.storeId, user?.store]);

  const loadAssets = async () => {
    try {
      const userStr = localStorage.getItem('authUser');
      let storesFilter: string[] | null = null;

      if (userStr) {
        try {
          const user = JSON.parse(userStr);

          // Decision B (§3.2, §17.1-B): admin e cfo veem todas as lojas sem filtro
          const isWildcard = (v: string) => v === 'all' || v.toLowerCase() === 'todos';
          if (user.type === 'admin' || user.type === 'cfo') {
            // Não enviamos filtro de loja - backend usará RBAC/wildcard do token
            storesFilter = null;
          } else if (user.stores === 'all' || user.stores === 'todos') {
            // Não enviamos filtro de loja - backend usará RBAC/wildcard do token
            storesFilter = null;
          } else if (Array.isArray(user.stores) && user.stores.length > 0 && !user.stores.every(isWildcard)) {
            // Múltiplas lojas específicas (excluindo wildcards)
            storesFilter = user.stores.filter((s: string) => !isWildcard(s));
          } else if (user.store) {
            // Compatibilidade com versões antigas que utilizavam campo único "store"
            storesFilter = [user.store];
          }
        } catch {
          // Em caso de erro no parse, deixamos storesFilter = null e deixamos o backend aplicar RBAC
        }
      }

      const params: { store?: string[] } = {};

      if (storesFilter && storesFilter.length > 0) {
        // Normalizar nomes das lojas para garantir consistência
        params.store = storesFilter.map((store: string) => store.trim().toLowerCase());
      }
      // Quando storesFilter é null ou vazio, não enviamos "store" para usar o RBAC no backend

      // Buscar ativos (com ou sem filtro de loja, conforme calculado acima)
      const response = await apiService.searchAssets(params);

      if (!response) {
        throw new Error('Resposta da API é undefined/null');
      }

      if (Array.isArray(response)) {
        setAssets(response);
      } else {
        setAssets([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar equipamentos');
    } finally {
      setLoading(false);
    }
  };

  const loadRequestData = async (requestId: string) => {
    setLoading(true);
    try {
      const response = await apiService.getMaintenanceRequest(requestId);
      
      // DIAGNÓSTICO: Verificar se response existe e tem estrutura esperada

      // Validação defensiva: verificar se response existe antes de acessar propriedades
      if (!response) {
        throw new Error(`API retornou resposta vazia para requestId: ${requestId}`);
      }
      
      const rawStatusHistory = Array.isArray(response.statusHistory) ? response.statusHistory : [];
      type RawHistoryItem = {
        status?: string;
        timestamp?: string;
        authorId?: string;
        authorRole?: string;
        notes?: string;
        autoTriggered?: boolean;
        source?: string;
        transitionType?: string;
        previousStatus?: string;
        metadata?: Record<string, unknown>;
      };
      const statusHistoryEntries: MaintenanceStatusEntry[] = rawStatusHistory.map((historyItem) => {
        const hi = historyItem as RawHistoryItem;
        if (!hi || !hi.status) {
          const fallbackTimestamp = hi?.timestamp ? new Date(hi.timestamp) : new Date();
          return {
            status: 'AGUARDANDO_TRIAGEM',
            authorId: hi?.authorId ?? 'unknown',
            authorRole: hi?.authorRole ?? 'sistema',
            timestamp: fallbackTimestamp,
            notes: hi?.notes,
            autoTriggered: hi?.autoTriggered ?? false,
            source: hi?.source,
            transitionType: hi?.transitionType,
            previousStatus: hi?.previousStatus,
            metadata: hi?.metadata,
          } as MaintenanceStatusEntry;
        }

        const { timestamp, ...rest } = hi;

        return {
          ...rest,
          timestamp: timestamp ? new Date(timestamp) : new Date(),
          autoTriggered: hi.autoTriggered ?? false,
          source: hi.source,
          transitionType: hi.transitionType,
          previousStatus: hi.previousStatus,
          metadata: hi.metadata,
        } as MaintenanceStatusEntry;
      });

      const responsePartners = Array.isArray(response.partners) ? response.partners : [];
      const normalizedAsset = response.asset ? { ...response.asset } : undefined;

      if (normalizedAsset) {
        const assetPartners = Array.isArray(normalizedAsset.partners) ? normalizedAsset.partners : [];
        const mergedPartners =
          responsePartners.length > 0
            ? responsePartners
            : assetPartners.length > 0
              ? assetPartners
              : normalizedAsset.partner
                ? [normalizedAsset.partner]
                : [];
        if (mergedPartners.length > 0) {
          normalizedAsset.partners = mergedPartners;
          if (!normalizedAsset.partner) {
            normalizedAsset.partner = mergedPartners[0];
          }
        }
      }

      const resolvedPartners =
        responsePartners.length > 0
          ? responsePartners
          : normalizedAsset?.partners
            ? normalizedAsset.partners
            : normalizedAsset?.partner
              ? [normalizedAsset.partner]
              : [];

      const resolvedPartnerId =
        response.partnerId ??
        normalizedAsset?.partnerId ??
        (resolvedPartners.length > 0 ? resolvedPartners[0]?.partnerId : undefined);

      const resolvedPartnerNotes =
        response.partnerNotes ??
        resolvedPartners.find(partner => partner?.notes)?.notes ??
        resolvedPartners.find(partner => partner?.observations)?.observations ??
        undefined;

      // Convert MaintenanceRequestResponse to MaintenanceItem
      const data: MaintenanceRequest = {
        id: response.id,
        equipmentId: response.equipmentId,
        equipmentDescription: response.equipmentDescription,
        type: response.type,
        requestDate: new Date(response.requestDate),
        requesterId: response.requesterId,
        problemDescription: response.problemDescription,
        urgency: response.urgency,
        estimatedCost: response.estimatedCost,
        approvedAmount: response.approvedAmount,
        approverId: response.approverId,
        approvalDate: response.approvalDate ? new Date(response.approvalDate) : undefined,
        approvalComments: response.approvalComments,
        supplierContacted: response.supplierContacted,
        attachmentUrls: response.attachmentUrls,
        responsiblePersonId: response.responsiblePersonId,
        requester: response.requester,
        approver: response.approver,
        asset: normalizedAsset,
        partners: resolvedPartners,
        
        // Campos específicos de MaintenanceRequest
        status: response.status,
        statusHistory: statusHistoryEntries,
        executedAt: response.executedAt ? new Date(response.executedAt) : undefined,
        actualCost: response.actualCost,
        scheduledArrangementDate: response.scheduledArrangementDate ? new Date(response.scheduledArrangementDate) : undefined,
        executionNotes: response.partnerNotes,
        partnerId: resolvedPartnerId,
        partnerNotes: resolvedPartnerNotes,
        // autoApprovalSource: a fonte de aprovação da própria requisição tem
        // precedência quando é um valor específico do request (ex.: compra
        // auto-aprovada por baixo valor). Caso contrário, usa o modo do perfil
        // de aprovação do ativo (comportamento legado).
        autoApprovalSource: response.autoApprovalSource === 'AUTO_PURCHASE_LOW_VALUE'
          ? 'AUTO_PURCHASE_LOW_VALUE'
          : (response.autoApproval?.mode ?? 'ADMIN_ONLY'),
        autoApprovalThreshold: response.autoApproval?.threshold ?? undefined,
        updatesMerged: '',
        reminderMetadata: undefined,
        updates: Array.isArray(response.updates)
          ? response.updates.map(update => ({
              id: update.id,
              requestId: update.requestId ?? response.id,
              authorId: update.authorId ?? 'UNKNOWN',
              authorName: update.authorName,
              message: update.message,
              timestamp: new Date(update.timestamp),
              source: (update.source ?? 'USER') as MaintenanceUpdate['source'],
              mergedFormatted: update.mergedFormatted ?? update.formattedLine ?? '',
              formattedLine: update.formattedLine
            }))
          : [],
        
        // Campos de execução e conclusão
        rating: response.rating,
        finalObservations: response.finalObservations,
        solutionDescription: response.solutionDescription,
        responsibleTechnician: response.responsibleTechnician,
        
        // Campos adicionais
        requesterRole: response.requesterRole,
        currentOwnerId: response.currentOwnerId,
        storeId: response.storeId,
        department: response.department,
        createdAt: response.createdAt ? new Date(response.createdAt) : undefined,
        updatedAt: new Date(response.updatedAt),

        // Campos de triagem e cotações
        subcategory: response.subcategory || null,
        triageDecision: (response.triageDecision || undefined) as MaintenanceRequest['triageDecision'],
        triagedBy: response.triagedBy,
        triagedAt: response.triagedAt,
        triageNotes: response.triageNotes,
        quotations: response.quotations || [],
        quotationsFulfilled: response.quotationsFulfilled ?? computeQuotationsFulfilled(response.quotations || []),
        selectedQuotationId: response.selectedQuotationId || undefined,
        quotationEmailsSentAt: response.quotationEmailsSentAt,
        quotationEmailsSentBy: response.quotationEmailsSentBy,
        cfoApprovalDate: response.cfoApprovalDate,
        cfoApproverId: response.cfoApproverId,
        cfoApprovalNotes: response.cfoApprovalNotes,
        finalValueConfirmed: response.finalValueConfirmed || false,
        finalValueConfirmedBy: response.finalValueConfirmedBy,
        canManageQuotations: response.canManageQuotations || false,
        // Issue #180: flag `isOwner` recebida do backend. Quando a flag está
        // ausente nos payloads (versões antigas do backend), o UI mantém o
        // comportamento legado (staff sempre read-only).
        isOwner: response.isOwner ?? undefined,
        // Campos de notificação explícita ao CFO (issue #55/#102). Propagados
        // do response para que a UI possa esconder o botão "Submeter para
        // Aprovação" depois do primeiro envio (cfoNotifiedAt preenchido).
        cfoNotifiedAt: response.cfoNotifiedAt
          ? new Date(response.cfoNotifiedAt)
          : null,
        cfoNotifiedBy: response.cfoNotifiedBy ?? null,
        cfoNotifiedQuotationsCount: response.cfoNotifiedQuotationsCount ?? null,
      };

      // Type assertion to access properties safely
      const requestData = {
        ...data,
        canManagerApprove: response.canManagerApprove ?? data.canManagerApprove,
        canPerformTriage: (response as MaintenanceRequestResponse & { canPerformTriage?: boolean }).canPerformTriage ?? false,
        // Issue #180: propagar a flag `isOwner` do backend para o estado
        // local. Quando ausente (payloads antigos), `undefined` faz com que
        // o UI preserve o comportamento legado (staff sempre read-only).
        isOwner: (response as MaintenanceRequestResponse & { isOwner?: boolean }).isOwner ?? undefined,
      };
      const registrationData = data as unknown as MaintenanceRegistration;

      console.debug('[MaintenanceForm] Loaded request data', {
        requestId: requestData.id,
        status: requestData.status,
        autoApprovalMode: requestData.autoApprovalSource,
        autoApprovalThreshold: requestData.autoApprovalThreshold,
        canManagerApprove: requestData.canManagerApprove,
      });

      setCurrentRequest(requestData); // Store the current request data

      // Verificar se o status é APROVADO e mudar automaticamente para modo registration
      // EXCETO para staff, que deve permanecer em read-only (conforme PLANO-CORRECAO-STAFF-GERENTE-RBAC.md T4.2)
      if (requestData.status === 'APROVADO' && mode.type !== 'registration' && user?.type !== 'staff') {
        setMode({ type: 'registration', requestId });
      } else if (['EXECUTADO', 'CONCLUIDO', 'REJEITADO', 'ARQUIVADO'].includes(requestData.status) && mode.type === 'registration') {
        // Status já não é APROVADO — reverter para modo visualização read-only
        setMode({ type: 'request', isEdit: true });
        window.history.replaceState({}, '', `/manutencao/editar/${requestId}`);
      }

      setFormData(prev => ({
        ...prev,
        equipmentId: data.equipmentId,
        problemDescription: requestData.problemDescription || '',
        urgency: requestData.urgency || 'media',
        estimatedCost: requestData.estimatedCost != null ? String(requestData.estimatedCost) : undefined,
        executedAt: mode.type === 'registration' ? new Date() : (requestData.executedAt ? new Date(requestData.executedAt) : undefined),
        actualCost: registrationData.actualCost,
        solutionDescription: registrationData.solutionDescription || '',
        responsibleTechnician: registrationData.responsibleTechnician || '',
        rating: registrationData.rating,
        finalObservations: registrationData.finalObservations || '',
        attachmentUrls: [], // Clear attachmentUrls before loading from Drive
        
        // Carregar novos campos
        scheduledArrangementDate: requestData.scheduledArrangementDate ? new Date(requestData.scheduledArrangementDate) : undefined,
        executionNotes: requestData.executionNotes || '',
        partnerId: requestData.partnerId || undefined,
        partnerNotes: requestData.partnerNotes || '',
        
        // Campos de recorrência
        recurrenceSuggestion: undefined, // Clear on edit load, will be fetched when needed
        overrideReason: '', // Clear on edit load
      }));

      // Load existing attachments from Google Drive
      if (data && data.id) {
        const existingDriveFiles = await apiService.getMaintenanceFiles(data.id);
        const mappedAttachments: AttachmentInfo[] = (existingDriveFiles as Array<{
          id?: string;
          originalName?: string;
          name?: string;
          webViewLink?: string;
          size?: number;
        }>)
          .filter(file => Boolean(file.id && (file.originalName || file.name) && file.webViewLink))
          .map(file => ({
            id: file.id as string,
            name: (file.originalName || file.name) as string,
            webViewLink: file.webViewLink as string,
            size: file.size,
          }));

        setFormData(prev => ({
          ...prev,
          attachmentUrls: mappedAttachments,
        }));

        setAttachmentPreviews(prev => [
          ...prev.filter(item => item.file),
          ...mappedAttachments.map(att => ({
            preview: att.webViewLink,
            name: att.name,
            webViewLink: att.webViewLink,
          }))
        ]);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
};

/* eslint-disable react-hooks/exhaustive-deps */
// loadRequestData is intentionally excluded: it is a non-stable function defined inline; adding it would cause infinite re-renders
useEffect(() => {
  if (!isAuthenticated) {
    navigate('/login', { state: { from: location }, replace: true });
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const formType = urlParams.get('type');
  const requestId = urlParams.get('requestId');

  if (formType === 'registration' && requestId) {
    setMode({ type: 'registration', requestId });
    loadRequestData(requestId);
  } else if (id) {
    setMode({ type: 'request', isEdit: true });
    loadRequestData(id);
  } else {
    setMode({ type: 'request' });
    loadAssets();
  }
}, [isAuthenticated, navigate, id, location]);
/* eslint-enable react-hooks/exhaustive-deps */

/* eslint-disable react-hooks/exhaustive-deps */
// Effect para resolver o conjunto "irmão" de requisições para as setas de
// navegação (issue #208). Estratégia em duas camadas:
//   1. Se o navigationContext trouxe `visibleIds` suficientes (≥ currentIndex+1),
//      usamos directamente — sem chamada extra ao backend.
//   2. Caso contrário (lista paginada, contexto incompleto, deep-link sem state),
//      refazemos a query ao backend com os filtros aplicados + pageSize grande.
useEffect(() => {
  if (!id) {
    setSiblingNavigation(null);
    return;
  }

  if (!navigationContext) {
    setSiblingNavigation(null);
    return;
  }

  // Cenário 1: contexto completo veio do MaintenanceList.
  const visibleIds = Array.isArray(navigationContext.visibleIds)
    ? navigationContext.visibleIds
    : [];
  const currentIndexFromContext = typeof navigationContext.currentIndex === 'number'
    ? navigationContext.currentIndex
    : visibleIds.indexOf(id);

  if (visibleIds.length > 0 && currentIndexFromContext >= 0) {
    setSiblingNavigation({
      visibleIds,
      currentIndex: currentIndexFromContext,
    });
    return;
  }

  // Cenário 2: fallback — pedir ao backend a página completa do filtro.
  if (!navigationContext.filters) {
    setSiblingNavigation(null);
    return;
  }

  let cancelled = false;
  setSiblingNavigationLoading(true);

  const fetchSiblings = async () => {
    try {
      const filters = navigationContext.filters;
      const fallbackPageSize = 500;
      // Expande tokens sintéticos do filtro de status (issue #251) antes de
      // chamar a API — alinha com `useMaintenance.fetchFromAPI` e com
      // `MaintenanceList.buildSiblingRequestParams`. Sem esta expansão o
      // backend rejeita `APROVADO_MANUTENCAO` / `APROVADO_COMPRAS` (não
      // pertencem ao enum canónico do schema Joi de filtros).
      const expandedStatus = expandStatusFilter({
        status: filters?.status,
        subcategory: filters?.subcategory,
      });
      const params = {
        page: 1,
        limit: fallbackPageSize,
        status: expandedStatus.status,
        urgency: filters?.urgency,
        subcategory: expandedStatus.subcategory,
        responsibleId: filters?.responsibleId,
        search: filters?.search,
        sortBy: filters?.sortBy,
        sortOrder: filters?.sortOrder,
        startDate: filters?.dateRange?.start ? new Date(filters.dateRange.start).toISOString() : undefined,
        endDate: filters?.dateRange?.end ? new Date(filters.dateRange.end).toISOString() : undefined,
      } as Parameters<typeof apiService.getMaintenanceRequests>[0];

      const response = await apiService.getMaintenanceRequests(params);
      if (cancelled) return;

      const items = response.requests || response.items || [];
      if (items.length >= fallbackPageSize) {
        // O limite do fallback pode truncar o conjunto filtrado. Nesse caso a
        // navegação por setas fica limitada aos primeiros N irmãos — sinalizamos
        // para diagnóstico em vez de falhar silenciosamente (issue #208).
        console.warn(
          `[MaintenanceForm] Conjunto filtrado de irmãos truncado em ${fallbackPageSize} — ` +
            `as setas prev/next podem não cobrir todos os resultados do filtro.`,
        );
      }
      const newIds = items.map((it) => it.id);
      const idx = newIds.indexOf(id);
      if (idx < 0) {
        // O registo actual já não está no conjunto filtrado (ex.: foi apagado
        // entre a listagem e a abertura). Não atiramos o utilizador para um
        // registo não relacionado: desligamos as setas.
        if (!cancelled) {
          setSiblingNavigation(null);
          toast.error('A navegação entre requisições não está disponível para este registo.', {
            duration: 4000,
            position: 'top-right',
          });
        }
      } else {
        setSiblingNavigation({
          visibleIds: newIds,
          currentIndex: idx,
        });
      }
    } catch {
      // Em caso de falha, desliga silenciosamente a navegação para não bloquear o form.
      if (!cancelled) setSiblingNavigation(null);
    } finally {
      if (!cancelled) setSiblingNavigationLoading(false);
    }
  };

  fetchSiblings();

  return () => {
    cancelled = true;
  };
}, [id, navigationContextKey]);
/* eslint-enable react-hooks/exhaustive-deps */

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    const MAX_FILES = 10;
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/quicktime',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    const currentCount = (formData.attachments?.length || 0) + (formData.attachmentUrls?.length || 0);
    const remaining = MAX_FILES - currentCount;
    const filesToAdd = files.slice(0, remaining);

    if (files.length > remaining) {
      setError(`Máximo de ${MAX_FILES} arquivos permitidos. Apenas os primeiros ${remaining} foram adicionados.`);
    }

    const validFiles = filesToAdd.filter(file => {
      return allowedTypes.includes(file.type) && file.size <= MAX_FILE_SIZE;
    });

    if (validFiles.length !== filesToAdd.length) {
      setError('Alguns arquivos foram rejeitados. Tipos permitidos: PDF, JPG, JPEG, PNG, GIF, WEBP, MP4, MOV, DOC, DOCX, XLS, XLSX. Tamanho máximo: 10 MB');
    }

    const previews = validFiles.map(file => ({
      file,
      name: file.name,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
    }));

    setAttachmentPreviews(prev => [...prev, ...previews]);
    setFormData(prev => ({
      ...prev,
      attachments: [...(prev.attachments || []), ...validFiles],
    }));
  };

  const removeAttachment = (index: number, isExistingFile: boolean = false) => {
    if (isExistingFile) {
      const attachment = formData.attachmentUrls?.[index];
      if (attachment) {
        // Marcar arquivo para exclusão pendente
        const pendingDeletion = {
          id: attachment.id || `attachment-${index}`,
          name: attachment.name || `Anexo ${index + 1}`,
          originalIndex: index,
          webViewLink: attachment.webViewLink,
          size: attachment.size,
        };
        
        setPendingDeletions(prev => [...prev, pendingDeletion]);
        
        // Remover apenas do estado local (visual)
        setFormData(prev => ({
          ...prev,
          attachmentUrls: prev.attachmentUrls?.filter((_, i) => i !== index),
        }));
        
        toast.success('Anexo marcado para exclusão. Salve para confirmar.');
      }
    } else {
      // Para novos arquivos, manter lógica atual
      setAttachmentPreviews(prev => {
        const newPreviews = [...prev];
        if (newPreviews[index]?.preview && newPreviews[index].file) {
          URL.revokeObjectURL(newPreviews[index].preview);
        }
        newPreviews.splice(index, 1);
        return newPreviews;
      });

      setFormData(prev => ({
        ...prev,
        attachments: prev.attachments?.filter((_, i) => i !== index),
      }));
    }
  };

  // FUNÇÃO REMOVIDA - Nova abordagem não precisa de confirmação imediata
  /*
  const confirmRemoveAttachment = async () => {
    if (!attachmentToRemove) return;
    
    setIsRemovingAttachment(true);
    setAttachmentRemovalError(null);
    
    try {
      // Se for um arquivo existente, chamar a API para remover
      if (attachmentToRemove.isExistingFile && attachmentToRemove.id) {
        await apiService.deleteFile(attachmentToRemove.id);
        toast.success('Anexo removido com sucesso');
      }
      
      // Remover do estado local
      if (attachmentToRemove.isExistingFile) {
        setFormData(prev => ({
          ...prev,
          attachmentUrls: prev.attachmentUrls?.filter((_, i) => i !== attachmentToRemove.index),
        }));
      } else {
        setAttachmentPreviews(prev => {
          const newPreviews = [...prev];
          if (newPreviews[attachmentToRemove.index]?.preview && newPreviews[attachmentToRemove.index].file) {
            URL.revokeObjectURL(newPreviews[attachmentToRemove.index].preview);
          }
          newPreviews.splice(attachmentToRemove.index, 1);
          return newPreviews;
        });

        setFormData(prev => ({
          ...prev,
          attachments: prev.attachments?.filter((_, i) => i !== attachmentToRemove.index),
        }));
      }
      
      // Fechar modal
      setAttachmentToRemove(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao remover anexo';
      setAttachmentRemovalError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsRemovingAttachment(false);
    }
  };
  */

  const cancelAllPendingDeletions = () => {
    // Restaurar anexos marcados para exclusão
    const restoredAttachments = pendingDeletions.map(pending => ({
      id: pending.id,
      name: pending.name,
      webViewLink: pending.webViewLink
    }));
    
    setFormData(prev => ({
      ...prev,
      attachmentUrls: [...(prev.attachmentUrls || []), ...restoredAttachments]
    }));
    
    setPendingDeletions([]);
    toast.success('Exclusões canceladas');
  };

  const uploadFiles = async (maintenanceId: string): Promise<AttachmentInfo[]> => {
    if (!formData.attachments?.length) return [];

    setUploadingFiles(true);
    const uploadedFilesInfo: AttachmentInfo[] = [];

    try {
      for (let i = 0; i < formData.attachments.length; i++) {
        const file = formData.attachments[i];
        const formData_upload = new FormData();
        formData_upload.append('files', file);
        formData_upload.append('resourceType', mode.type === 'request' ? 'maintenance_request' : 'maintenance_registration');
        formData_upload.append('resourceId', maintenanceId);

        const response = await apiService.uploadFiles(formData_upload, (progress) => {
          setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
        });

        if (response.data && response.data.files && response.data.files.length > 0) {
          uploadedFilesInfo.push(...response.data.files
            .map((f: { id?: string; originalName?: string; name?: string; webViewLink?: string }) => ({
              id: f.id,
              name: f.originalName || f.name,
              webViewLink: f.webViewLink,
            }))
            .filter((att): att is AttachmentInfo => Boolean(att.id && att.name && att.webViewLink)));
        }
      }

      return uploadedFilesInfo;
    } catch (error) {
      throw new Error(`Erro no upload: ${error instanceof Error ? error.message : (typeof (error as Record<string, unknown>)?.message === 'string' ? (error as Record<string, unknown>).message : 'Erro desconhecido')}`);
    } finally {
      setUploadingFiles(false);
      setUploadProgress({});
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Se há exclusões pendentes, mostrar modal de confirmação
    if (pendingDeletions.length > 0) {
      setShowPendingDeletionsModal(true);
      return;
    }

    // Se for conclusão (registration), mostrar modal de confirmação primeiro
    if (mode.type === 'registration') {
      setShowCompletionModal(true);
      return;
    }

    // processSubmit re-lança o erro para que CompletionModal possa abortar o
    // sucesso. Aqui (callback de <form onSubmit>) a Promise ficaria pendurada
    // e o React não a consome, causando "unhandledrejection" no Sentry
    // (Object captured as promise rejection with keys: code, error).
    // setError já foi chamado dentro de processSubmit; basta engolir.
    try {
      await processSubmit();
    } catch {
      // already surfaced via setError; intentional swallow
    }
  };

  const processPendingDeletions = async (): Promise<void> => {
    if (pendingDeletions.length === 0) return;

    try {
      // Processar exclusões pendentes
      for (const deletion of pendingDeletions) {
        await apiService.deleteFile(deletion.id);
      }
      
      // Limpar exclusões pendentes
      setPendingDeletions([]);
      
      toast.success(`${pendingDeletions.length} anexo(s) excluído(s) com sucesso`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao excluir anexos';
      toast.error(errorMessage);
      throw error; // Re-throw para parar o processo de salvamento
    }
  };

  const confirmPendingDeletionsAndSaveOnly = async () => {
    setShowPendingDeletionsModal(false);
    
    try {
      // Primeiro processar exclusões pendentes
      await processPendingDeletions();

      // Depois continuar com o salvamento simples (sem redirecionar)
      await handleSave();
    } catch {
      // Se houver erro nas exclusões, não continuar com o salvamento
    }
  };

  const showSuccessToastAndRedirect = (message: string, redirectPath: string = '/manutencao') => {
    toast.success(message, {
      duration: 3000,
      position: 'top-right',
    });
    setTimeout(() => {
      navigate(redirectPath, { state: location.state });
    }, 1500);
  };

  const handleBackToList = useCallback(() => {
    navigate('/manutencao', { state: location.state });
  }, [navigate, location.state]);

  const processSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      // Obter a descrição do equipamento selecionado
      const selectedAsset = getSelectedAsset();
      const equipmentDescription = selectedAsset?.description || formData.equipmentId || '';

      const payload = {
        ...formData,
        equipmentId: formData.equipmentId,
        problemDescription: formData.problemDescription.trim(),
        urgency: formData.urgency,
        estimatedCost: (() => { const v = parseFloat(String(formData.estimatedCost ?? '')); return isNaN(v) ? undefined : v; })(),
        actualCost: (() => { const v = parseFloat(String(formData.actualCost ?? '')); return isNaN(v) ? undefined : v; })(),
        solutionDescription: formData.solutionDescription?.trim(),
        responsibleTechnician: formData.responsibleTechnician?.trim(),
        rating: formData.rating,
        finalObservations: formData.finalObservations?.trim(),
        equipmentDescription, // Adicionando a descrição do equipamento
        
        // Novos campos para o payload
        scheduledArrangementDate: formData.scheduledArrangementDate?.toISOString(),
        executionNotes: formData.executionNotes?.trim(),
        executedAt: formData.executedAt?.toISOString(),
        partnerId: formData.partnerId,
        
        // Campos de recorrência
        overrideReason: formData.overrideReason?.trim(),
      };

      let maintenanceId: string;
      let newUploadedFiles: AttachmentInfo[] = [];

      // Track metrics for recurrence acknowledgment/override (T037)
      if (formData.recurrenceSuggestion) {
        const metricAction = formData.recurrenceSuggestion.hasConflict ? 'override_suggestion' : 'acknowledge_suggestion';
        trackRecurrenceMetric(metricAction, {
          equipmentId: formData.equipmentId,
          hasRecurrence: true,
          hasConflict: formData.recurrenceSuggestion.hasConflict,
          suggestedDate: formData.recurrenceSuggestion.recurrence.nextDueDate,
          selectedDate: formData.scheduledArrangementDate?.toISOString().split('T')[0],
          overrideReasonProvided: !!formData.overrideReason,
          overrideReasonLength: formData.overrideReason?.length || 0,
        });
      }

      if (mode.type === 'registration' && mode.requestId) {
        if (formData.attachments?.length) {
          newUploadedFiles = await uploadFiles(mode.requestId);
        }

        // Combine existing attachment URLs with newly uploaded ones AFTER upload
        const allAttachmentUrls = [...(formData.attachmentUrls || []), ...newUploadedFiles].map(att => att.webViewLink);

        const completed = await apiService.completeMaintenanceRequest(mode.requestId, {
          executionDate: payload.executedAt!, // Usar executedAt em vez de executionDate
          actualCost: payload.estimatedCost!, // Custo preenchido pelo responsável é estimado; custo real é definido pelo CFO ao concluir
          solutionDescription: payload.solutionDescription!,
          responsibleTechnician: payload.responsibleTechnician!,
          rating: payload.rating,
          finalObservations: payload.finalObservations,
          attachmentUrls: allAttachmentUrls
        });
        invalidateMaintenanceCache('complete', completed.id, completed.asset?.store);
        toast.success(isPurchaseRequest ? 'Compra registada com sucesso!' : 'Execução registada com sucesso!', { duration: 3000, position: 'top-right' });
        // Mudar para modo visualização (read-only) e limpar URL de registration
        setMode({ type: 'request', isEdit: true });
        window.history.replaceState({}, '', `/manutencao/editar/${mode.requestId}`);
        await loadRequestData(mode.requestId);
      } else if (mode.isEdit && id) {
        if (formData.attachments?.length) {
          newUploadedFiles = await uploadFiles(id);
        }

        // Combine existing attachment URLs with newly uploaded ones AFTER upload
        const allAttachmentUrls = [...(formData.attachmentUrls || []), ...newUploadedFiles].map(att => att.webViewLink);

        const updatePayload: Record<string, unknown> = {
          equipmentId: payload.equipmentId,
          urgency: payload.urgency,
          estimatedCost: payload.estimatedCost,
          attachmentUrls: allAttachmentUrls
        };
        if (payload.problemDescription) {
          updatePayload.problemDescription = payload.problemDescription;
        }
        const updated = await apiService.updateMaintenanceRequest(id, updatePayload);
        invalidateMaintenanceCache('update', updated.id, updated.asset?.store);
        showSuccessToastAndRedirect(formData.subcategory === 'compras' ? 'Requisição de compra atualizada com sucesso!' : 'Requisição de manutenção atualizada com sucesso!');
      } else {
        // Creating new request
        const createPayload: {
          equipmentId?: string
          problemDescription?: string
          urgency?: string
          estimatedCost?: number
          equipmentDescription?: string
          type: string
          status?: string
          executedAt?: string
          solutionDescription?: string | null
          responsibleTechnician?: string | null
          partnerId?: string | null
          rating?: number | null
          finalObservations?: string | null
          subcategory?: string | null
        } = {
          equipmentId: payload.equipmentId,
          problemDescription: payload.problemDescription,
          urgency: payload.urgency,
          estimatedCost: payload.estimatedCost,
          equipmentDescription: payload.equipmentDescription,
          type: formData.type || 'requisicao', // Always send type so backend can determine triage
        };

        // If creating a "Registo de Execução" (registration type), include execution fields
        if (formData.type === 'registo') {
          createPayload.status = 'EXECUTADO'; // Set initial status to EXECUTADO for registrations
          createPayload.executedAt = payload.executedAt;
          createPayload.estimatedCost = payload.estimatedCost;
          // Convert empty strings to null for optional fields (backend validation rejects empty strings)
          createPayload.solutionDescription = payload.solutionDescription?.trim() || null;
          createPayload.responsibleTechnician = payload.responsibleTechnician?.trim() || null;
          createPayload.partnerId = payload.partnerId || null;
          createPayload.rating = payload.rating || null;
          createPayload.finalObservations = payload.finalObservations?.trim() || null;
          // Use solutionDescription as problemDescription for validation (registration doesn't have a problem, only a solution)
          const descriptionText = payload.solutionDescription?.trim() || formData.solutionDescription?.trim() || 'Registo de manutenção executada';
          createPayload.problemDescription = descriptionText;
        } else {
          // For "Situação por Resolver", always include subcategory (required by backend)
          createPayload.subcategory = formData.subcategory || null;
        }

        const response = await apiService.createMaintenanceRequest(createPayload as Parameters<typeof apiService.createMaintenanceRequest>[0]);

        maintenanceId = response.id;

        // O upload faz o snapshot dos webViewLinks em `attachmentUrls`
        // server-side (POST /files/upload), então não fazemos mais um PUT
        // /maintenance/requests/{id} aqui — ele retornava 403 quando o
        // backend já tinha auto-aprovado a requisição (gerente em ativo
        // gerente_threshold ou sem nível: status inicial = APROVADO, e
        // canModifyRequest exige PENDENTE). Era a causa raiz do Sentry
        // DOBECO-OPERACAO-E.
        if (formData.attachments?.length) {
          newUploadedFiles = await uploadFiles(maintenanceId);
        }
        invalidateMaintenanceCache('create', response.id, response.asset?.store);
        showSuccessToastAndRedirect(formData.subcategory === 'compras' ? 'Requisição de compra criada com sucesso!' : 'Requisição de manutenção criada com sucesso!');
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
      throw err; // Propagar o erro para o CompletionModal não mostrar sucesso falso
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('Cost')
        ? value.replace(/,/g, '.')
        : name === 'rating' ? (value ? Number(value) : undefined) : value,
    }));

    // Fetch recurrence suggestion when equipment changes
    if (name === 'equipmentId' && value) {
      // Clear previous recurrence data
      setFormData(prev => ({
        ...prev,
        recurrenceSuggestion: undefined,
        overrideReason: '',
      }));
      
      // Fetch new recurrence suggestion if we have a selected date
      if (formData.scheduledArrangementDate) {
        fetchRecurrenceSuggestion(value, formData.scheduledArrangementDate);
      } else {
        // Fetch without date to show next due date
        fetchRecurrenceSuggestion(value);
      }
    } else if (name === 'equipmentId' && !value) {
      // Clear recurrence data when equipment is cleared
      setFormData(prev => ({
        ...prev,
        recurrenceSuggestion: undefined,
        overrideReason: '',
      }));
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newDate = value ? new Date(value) : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: newDate,
    }));

    // Check for recurrence conflicts when date changes
    if (name === 'scheduledArrangementDate' && formData.equipmentId && newDate) {
      checkRecurrenceConflict(formData.equipmentId, newDate);
    }
  };

  // Função para buscar sugestão de recorrência da API
  const fetchRecurrenceSuggestion = async (equipmentId: string, requestedDate?: Date) => {
    if (!equipmentId) return;

    setLoadingRecurrence(true);
    setRecurrenceError(null);

    try {
      const requestedDateStr: string | undefined = requestedDate
        ? requestedDate.toISOString().split('T')[0]
        : undefined;

      const response = await apiService.getNextRecurrenceOccurrence(equipmentId, requestedDateStr);

      if (response) {
        const recurrenceRaw = response.recurrence;
        const recurrence = {
          quantity: recurrenceRaw.quantity ?? 0,
          unit: recurrenceRaw.unit ?? ('d' as const),
          lastExecutionDate: recurrenceRaw.lastExecutionDate,
          nextDueDate: recurrenceRaw.nextDueDate,
          recurrenceString: recurrenceRaw.recurrenceString ?? '',
          inactive: recurrenceRaw.inactive ?? false,
        };

        // Check if there's a conflict with the selected date
        const hasConflict = requestedDate && recurrence.nextDueDate
          ? requestedDate.toISOString().split('T')[0] !== recurrence.nextDueDate
          : false;

        const conflictMessage = hasConflict
          ? `Data sugerida para próxima manutenção: ${formatDate(recurrence.nextDueDate)}`
          : undefined;

        setFormData(prev => ({
          ...prev,
          recurrenceSuggestion: {
            recurrence,
            hasConflict,
            conflictMessage,
          },
        }));

        // Track metrics - user viewed recurrence suggestion
        trackRecurrenceMetric('view_suggestion', {
          equipmentId,
          hasRecurrence: true,
          hasConflict,
          suggestedDate: recurrence.nextDueDate,
          requestedDate: requestedDate?.toISOString().split('T')[0],
        });
      }
    } catch (error) {
      setRecurrenceError('Não foi possível carregar informações de recorrência para este ativo.');
      
      // Track metrics - error loading recurrence
      trackRecurrenceMetric('error_loading', {
        equipmentId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setLoadingRecurrence(false);
    }
  };

  // Função para verificar conflitos de recorrência
  const checkRecurrenceConflict = async (equipmentId: string, selectedDate: Date) => {
    await fetchRecurrenceSuggestion(equipmentId, selectedDate);
  };

  // Função para enviar métricas de recorrência (T037)
  const trackRecurrenceMetric = async (_action: string, _metadata: Record<string, unknown>) => {
    try {
      // TODO: Implementar chamada à API de métricas quando disponível
      // Por enquanto, apenas log no console para desenvolvimento

      // Futuramente:
      // await apiService.trackRecurrenceMetric(action, metadata);
    } catch {
      // Silently fail - metrics shouldn't break user experience
    }
  };

  // Função auxiliar para formatar datas
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('pt-PT');
    } catch {
      return dateString;
    }
  };

  const getSelectedAsset = () => {
    // Tenta encontrar no array de assets (para novas requisições)
    const assetFromList = assets.find(asset => asset.equipmentId === formData.equipmentId);
    if (assetFromList) {
      return assetFromList;
    }
    
    // Fallback para o asset da requisição atual (para edições/registration)
    if (currentRequest?.asset && currentRequest.asset.equipmentId === formData.equipmentId) {
      return currentRequest.asset;
    }
    
    return null;
  };

  const getSelectedAssetPartners = () => {
    const asset = getSelectedAsset();
    // Return partners array if available, otherwise fall back to single partner
    if (asset?.partners && asset.partners.length > 0) {
      return asset.partners;
    }
    // Fallback to single partner for backward compatibility
    return asset?.partner ? [asset.partner] : [];
  };

  // Type guards for discriminated union (MaintenanceRequest | MaintenanceRegistration)
  const isRequest = (item: MaintenanceItem | null): item is MaintenanceRequest => {
    return !!item && (item as MaintenanceRequest).type === 'requisicao';
  };

  const hasCompletionData = (item: MaintenanceItem | null): item is MaintenanceRegistration => {
    return !!item && 'executionDate' in item;
  };

  const canSubmit = () => {
    if (mode.type === 'request') {
      const basicRequirements = formData.equipmentId && formData.problemDescription.trim().length >= 10;

      if (!basicRequirements) return false;

      // Check recurrence conflict validation
      if (formData.recurrenceSuggestion?.hasConflict) {
        // Override reason is required when there's a conflict
        if (!formData.overrideReason || formData.overrideReason.trim().length < 15) {
          return false;
        }
      }

      return true;
    } else {
      // For completion/registration mode, check basic requirements and cost validation
      // No modo registration, o custo preenchido é sempre estimatedCost (actualCost é definido pelo CFO ao concluir)
      const costValue = parseFloat(String(formData.estimatedCost ?? ''));
      const basicRequirements =
        formData.executedAt &&
        formData.solutionDescription &&
        formData.solutionDescription.trim().length >= 20 &&
        formData.responsibleTechnician &&
        (isNaN(costValue) || costValue >= 0);

      if (!basicRequirements) {
        return false;
      }

      // Check if estimated cost exceeds limits that would require re-approval
      const estimatedCost = parseFloat(String(formData.estimatedCost || '')) || 0;

      if (isRequest(currentRequest)) {
        const request = currentRequest as MaintenanceRequest;

        // Para compras, exigir cotação aprovada pelo CFO antes de registar execução
        if (request.subcategory === 'compras' && !request.selectedQuotationId) {
          return false;
        }

        // Para ativos auto-aprovados (approverId='system'), não há limite de custo
        const isAutoApproved = request.approverId === 'system';

        if (!isAutoApproved) {
          // O limite aprovado é o estimatedCost (tanto para aprovação formal quanto gerente com threshold)
          const approvedLimit = request.estimatedCost || 0;

          // If estimated cost exceeds the approved limit, prevent completion (with 1 cent tolerance)
          if (approvedLimit > 0 && estimatedCost > approvedLimit + 0.01) {
            return false;
          }
        }
      }

      return true;
    }
  };

  // Helper function to get detailed validation status for completion
  const getCompletionValidationDetails = () => {
    if (mode.type !== 'registration') {
      return null;
    }

    // No modo registration, o custo é sempre estimatedCost (actualCost é definido pelo CFO ao concluir)
    const costValue = parseFloat(String(formData.estimatedCost || '')) || 0;

    let costLimit = 0;
    let costLimitLabel = '€0';
    let isAutoApproved = false;

    if (isRequest(currentRequest)) {
      const request = currentRequest as MaintenanceRequest;
      isAutoApproved = request.approverId === 'system';

      if (isAutoApproved) {
        costLimitLabel = 'Sem limite (auto-aprovado)';
      } else {
        costLimit = request.estimatedCost || 0;
        const hasFormalApproval = request.approverId && request.approverId !== 'system';
        costLimitLabel = hasFormalApproval
          ? `€${costLimit.toFixed(2)} (aprovado)`
          : `€${costLimit.toFixed(2)}`;
      }
    }

    const checks: Record<string, { valid: boolean; label: string; message: string }> = {
      hasExecutedAt: {
        valid: !!formData.executedAt,
        label: isPurchaseRequest ? 'Data da compra' : 'Data de execução',
        message: formData.executedAt ? 'Preenchida' : 'Obrigatória',
      },
      hasActualCost: {
        valid: true,
        label: isPurchaseRequest ? 'Valor da compra' : 'Custo estimado',
        message: costValue > 0 ? `€${costValue.toFixed(2)}` : 'Opcional',
      },
      hasSolutionDescription: {
        valid: !!formData.solutionDescription && formData.solutionDescription.trim().length >= 20,
        label: isPurchaseRequest ? 'Descrição da compra' : 'Descrição da solução',
        message: formData.solutionDescription
          ? `${formData.solutionDescription.trim().length}/20 caracteres`
          : 'Obrigatória (mínimo 20 caracteres)',
      },
      hasResponsibleTechnician: {
        valid: !!formData.responsibleTechnician,
        label: 'Parceiro responsável',
        message: formData.responsibleTechnician ? 'Selecionado' : 'Obrigatório',
      },
    };

    // Só mostrar validação de limite de custo para ativos com aprovação formal (não AUTO)
    if (!isAutoApproved && costLimit > 0) {
      checks.costWithinLimit = {
        valid: costValue <= costLimit + 0.01,
        label: 'Custo dentro do limite',
        message: `€${costValue.toFixed(2)} / ${costLimitLabel}`,
      };
    }

    // Validação de cotação aprovada para compras
    if (isRequest(currentRequest)) {
      const request = currentRequest as MaintenanceRequest;
      if (request.subcategory === 'compras') {
        checks.quotationApproved = {
          valid: !!request.selectedQuotationId,
          label: 'Cotação aprovada pelo CFO',
          message: request.selectedQuotationId ? 'Aprovada' : 'Pendente - aguarda aprovação CFO',
        };
      }
    }

    return checks;
  };

  // Permission and action control functions
  // Issue #180: staff/supervisor NUNCA vê botões de ação em requisições
  // de terceiros (isOwner === false). Apenas donos podem executar ações,
  // embora possam adicionar comentários via UpdatesModal.
  const isActionAllowed = () => {
    if (!user || !isRequest(currentRequest)) return true;
    if (user.type !== 'staff' && user.type !== 'supervisor') return true;
    // Fail-closed: exige posse PROVADA. Se a flag vier `undefined` (backend
    // antigo, endpoint que não a popula, resposta em cache), a posse não está
    // provada e a ação fica bloqueada.
    return currentRequest.isOwner === true;
  };

  const canApprove = () => {
    console.debug('[MaintenanceForm] canApprove check', {
      userType: user?.type,
      requestId: currentRequest?.id,
      requestStatus: currentRequest?.status,
      requestType: currentRequest?.type,
      canManagerApproveFlag: currentRequest?.type === 'requisicao' ? (currentRequest as MaintenanceRequest)?.canManagerApprove : undefined,
    });

    if (!user || !currentRequest) {
      return false;
    }
    if (currentRequest.type !== 'requisicao' || currentRequest.status !== 'PENDENTE') {
      return false;
    }

    // Compras: a aprovação é feita pelo CFO via cotação, não via este botão
    if (isRequest(currentRequest) && (currentRequest as MaintenanceRequest).subcategory === 'compras') {
      return false;
    }

    // Admin sempre pode aprovar
    if (user.type === 'admin') {
      return true;
    }

    // Staff nunca pode aprovar
    if (user.type !== 'gerente') {
      return false;
    }

    // Gerente: usar flag calculada pelo backend
    return currentRequest.canManagerApprove === true;
  };

  const canComplete = () => {
    if (!user || !currentRequest) {
      return false;
    }
    if (currentRequest.type !== 'requisicao' || (currentRequest.status !== 'APROVADO' && currentRequest.status !== 'EXECUTADO')) {
      return false;
    }
    if (!isActionAllowed()) {
      return false;
    }
    return user.type === 'cfo' || user.type === 'admin';
  };

  const canTransfer = () => {
    if (!user || !currentRequest || currentRequest.type !== 'requisicao') {
      return false;
    }
    // Não permitir transferência em requisições concluídas ou rejeitadas
    if (currentRequest.status === 'CONCLUIDO' || currentRequest.status === 'REJEITADO') {
      return false;
    }
    if (!isActionAllowed()) {
      return false;
    }
    // Gerentes, cfo e admins podem transferir
    return user.type === 'gerente' || user.type === 'cfo' || user.type === 'admin';
  };

  const canMarkExecuted = () => {
    if (!user || !currentRequest) {
      return false;
    }
    if (currentRequest.type !== 'requisicao' || currentRequest.status !== 'APROVADO') {
      return false;
    }
    if (!isActionAllowed()) {
      return false;
    }
    // Gerente, cfo e admin podem marcar como executado
    return user.type === 'gerente' || user.type === 'cfo' || user.type === 'admin';
  };

  const canSetActualCost = () => {
    // Apenas CFO e Admin podem definir o custo real
    return user?.type === 'cfo' || user?.type === 'admin';
  };

  const formatCfoNotifiedAt = (value: Date | string | null | undefined): string => {
    if (!value) return '';
    try {
      const date = value instanceof Date ? new Date(value) : new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return '';
    }
  };

  /**
   * Indica se a requisição foi auto-aprovada por ser uma compra de baixo valor
   * (cotação única ≤ AUTO_APPROVE_PURCHASE_THRESHOLD). Nesses casos o botão
   * "Submeter para Aprovação" do CFO não faz sentido (já está APROVADO).
   */
  const isAutoApprovedLowValuePurchase = (request: MaintenanceRequest): boolean => {
    return request.status === 'APROVADO' && request.autoApprovalSource === 'AUTO_PURCHASE_LOW_VALUE';
  };


  const canShowSaveButton = () => {
    if (!user || !currentRequest || !mode.isEdit || mode.type !== 'request') {
      return false;
    }

    // Staff só pode editar REQ antes da triagem (estado AGUARDANDO_TRIAGEM)
    if (user.type === 'staff') {
      return currentRequest.status === 'AGUARDANDO_TRIAGEM' && !isReadOnlyMode();
    }

    // Compras em PENDENTE: o fluxo é gerir cotações, não editar campos
    if (isRequest(currentRequest) && (currentRequest as MaintenanceRequest).subcategory === 'compras' && currentRequest.status === 'PENDENTE') {
      return false;
    }

    const eligibleStatuses = ['PENDENTE', 'APROVADO', 'EXECUTADO'];
    return eligibleStatuses.includes(currentRequest.status?.toString().toUpperCase() || '') && !isReadOnlyMode();
  };


  const isPurchaseRequest = isRequest(currentRequest) && (currentRequest as MaintenanceRequest).subcategory === 'compras';

  // Em novas requisições (sem currentRequest carregado), recorre a formData.subcategory
  // para que o cabeçalho reaja imediatamente ao radio "Compras" escolhido pelo utilizador.
  const isPurchaseContext = isPurchaseRequest || (!currentRequest && formData.subcategory === 'compras');

  // Determina se estamos a editar uma requisição de compra em fase pré-registo
  // (AGUARDANDO_TRIAGEM, PENDENTE ou APROVADO). Nesse estado, escondemos vários
  // campos de manutenção (Urgência, Custo Estimado, Data Prevista, Recorrência,
  // Motivo de Sobreposição, Descrição do Problema, caixa de Configuração de
  // Aprovação) porque são irrelevantes para o fluxo de compras.
  const isPurchaseEditPending = (() => {
    if (!isPurchaseRequest) return false;
    if (!mode.isEdit || mode.type !== 'request') return false;
    const status = currentRequest?.status?.toString().toUpperCase();
    if (!status) return false;
    return ['AGUARDANDO_TRIAGEM', 'PENDENTE', 'APROVADO'].includes(status);
  })();

  // Manutenção em fase de triagem: durante o AGUARDANDO_TRIAGEM o pedido ainda
  // não foi classificado (pode virar compra via "Converter em Compra" no
  // TriageModal), por isso os campos de planeamento de manutenção são prematuros.
  const isMaintenancePreTriage =
    !isPurchaseRequest &&
    mode.isEdit &&
    mode.type === 'request' &&
    currentRequest?.status?.toString().toUpperCase() === 'AGUARDANDO_TRIAGEM';

  // Flag geral que cobre compras pré-registo (#46) e manutenção em triagem.
  // As versões read-only destes campos, nas secções superiores, permanecem visíveis.
  const shouldHideMaintenanceFields = isPurchaseEditPending || isMaintenancePreTriage;

  // Determina se devemos esconder o carrossel de parceiros do PartnerInfoPanel.
  // Aplica-se a qualquer fase do fluxo de compras (incluindo "Registar Compra").
  const shouldHidePartnerCarousel = isPurchaseRequest;

  const canViewQuotations = () => {
    if (!user || !currentRequest) return false;
    // Admin, CFO e Gestor veem sempre
    if (user.type === 'admin' || user.type === 'cfo' || user.type === 'gerente') return true;
    // Responsável da requisição vê (pode ser supervisor ou gestor)
    if (isRequest(currentRequest) && (currentRequest as MaintenanceRequest).responsiblePersonId === user.id) return true;
    return false;
  };

  const shouldShowPartnerPanel = () => {
    if (!formData.equipmentId || !getSelectedAsset()) {
      return false;
    }

    const status = currentRequest?.status?.toUpperCase();
    const req = isRequest(currentRequest) ? (currentRequest as MaintenanceRequest) : null;
    const isPurchase = req?.subcategory === 'compras';

    // Para novas requisições (sem status definido) em modo request, não mostrar
    // (será visível após triagem ou para compras)
    if (!status && mode.type === 'request') {
      return false;
    }

    if (!user) return false;

    // Para compras, em qualquer fase pré-arquivo (incluindo AGUARDANDO_TRIAGEM,
    // PENDENTE, APROVADO, EXECUTADO, CONCLUIDO e o modal "Registar Compra"), o
    // painel aparece para mostrar a secção de "Cotações Recebidas". O carrossel
    // de parceiros é ocultado via prop `hidePartnerCarousel`.
    if (isPurchase) {
      // Sem cotações registadas, não vale a pena mostrar o painel (vazio).
      if (!req || !Array.isArray(req.quotations) || req.quotations.length === 0) {
        return false;
      }
      if (status && ['CONCLUIDO', 'REJEITADO', 'ARQUIVADO'].includes(status)) {
        // Mantém-se visível mesmo após registo da compra se houver cotações.
        return true;
      }
      return true;
    }

    // Criação (sem status) ou AGUARDANDO_TRIAGEM: nunca mostrar (manutenção)
    if (!status || status === 'AGUARDANDO_TRIAGEM') {
      return false;
    }

    // PENDENTE: só compras (gestor/admin/assignedTo) — para manutenção não se mostra
    if (status === 'PENDENTE') {
      return false;
    }

    // Após triagem (APROVADO, EXECUTADO, CONCLUIDO):
    // - triageDecision === 'RESOLVE': responsável + gestor/admin/cfo
    // - triageDecision === 'EU_RESOLVO': apenas gestor/admin/cfo
    // - Sem triageDecision (compras APROVADO+, etc.): responsável + gestor/admin/cfo
    if (req && ['APROVADO', 'EXECUTADO', 'CONCLUIDO'].includes(status)) {
      if (req.triageDecision === 'EU_RESOLVO') {
        return user.type === 'gerente' || user.type === 'admin' || user.type === 'cfo';
      }
      return req.responsiblePersonId === user.id ||
        user.type === 'gerente' || user.type === 'admin' || user.type === 'cfo';
    }

    return false;
  };

  const isReadOnlyMode = () => {
    if (!currentRequest) return false;

    // Status terminais e executados são sempre read-only
    if (['CONCLUIDO', 'REJEITADO', 'EXECUTADO', 'ARQUIVADO'].includes(currentRequest.status)) {
      return true;
    }

    // Issue #180: O modo de leitura deriva exclusivamente da flag `isOwner`
    // emitida pelo backend. Quando `isOwner === false`, o utilizador NÃO é
    // dono da requisição e o formulário fica em modo somente-leitura,
    // independentemente do tipo de utilizador (staff, supervisor, gerente, etc.).
    if (
      mode.isEdit &&
      isRequest(currentRequest) &&
      currentRequest.isOwner === false
    ) {
      return true;
    }

    // Legacy fallback: when the backend does not emit `isOwner` (undefined)
    // staff/supervisor remain read-only — we cannot determine ownership and
    // must preserve the pre-#180 safe default.
    if (
      mode.isEdit &&
      user &&
      (user.type === 'staff' || user.type === 'supervisor') &&
      isRequest(currentRequest) &&
      currentRequest.isOwner === undefined
    ) {
      return true;
    }

    return false;
  };

  // Action handlers
  const handleApprove = () => {
    setShowApprovalModal(true);
  };

  const handleReject = () => {
    setShowRejectionModal(true);
  };

  const handleComplete = () => {
    if (currentRequest) {
      navigate(`/manutencao/novo?type=registration&requestId=${currentRequest.id}`);
    }
  };

  const handleSwitchToEditMode = () => {
    if (mode.requestId) {
      setMode({ type: 'request', isEdit: true, requestId: mode.requestId });
    }
  };

  const handleSwitchToRegistrationMode = () => {
    if (mode.requestId) {
      setMode({ type: 'registration', requestId: mode.requestId });
    }
  };

  // Issue #208 — navegação directa por setas entre requisições dentro do
  // filtro activo. Calcula o ID vizinho (anterior ou seguinte) com base
  // no `siblingNavigation` resolvido pelo effect acima. Devolve `null`
  // se a navegação não está disponível (sem contexto, sem lista, etc.).
  const getSiblingId = useCallback(
    (direction: 'prev' | 'next'): string | null => {
      if (!siblingNavigation) return null;
      const { visibleIds, currentIndex } = siblingNavigation;
      if (!Array.isArray(visibleIds) || visibleIds.length <= 1) return null;
      if (currentIndex < 0 || currentIndex >= visibleIds.length) return null;

      const targetIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= visibleIds.length) return null;

      return visibleIds[targetIndex] ?? null;
    },
    [siblingNavigation],
  );

  const previousSiblingId = getSiblingId('prev');
  const nextSiblingId = getSiblingId('next');
  const siblingPosition =
    siblingNavigation && siblingNavigation.currentIndex >= 0
      ? `${siblingNavigation.currentIndex + 1} de ${siblingNavigation.visibleIds.length}`
      : null;

  const navigateToSibling = useCallback(
    (siblingId: string | null) => {
      if (!siblingId || !siblingNavigation) return;
      // Mantemos o navigationContext na nova rota para que a navegação
      // em cadeia funcione: prev → detail(prev) → prev → detail(prev.prev).
      // IMPORTANTE: em vez de repassar o contexto literalmente, actualizamos
      // o `currentIndex` (e a lista de IDs resolvida) para reflectir o irmão
      // alvo — caso contrário o contador de posição e os botões ficam presos
      // no índice do registo anterior e a navegação encadeada nunca avança.
      const targetIndex = siblingNavigation.visibleIds.indexOf(siblingId);
      if (targetIndex < 0) return;
      navigate(`/manutencao/editar/${siblingId}`, {
        state: {
          // Forwarda o `listRestore` da PR #213 para que o "Voltar" da
          // listagem continue a restaurar filtros/ordenação/paginação mesmo
          // após navegar em cadeia entre irmãos.
          ...(locationState ?? {}),
          navigationContext: {
            ...(navigationContext ?? {}),
            visibleIds: siblingNavigation.visibleIds,
            currentIndex: targetIndex,
          },
        },
      });
    },
    [navigate, locationState, navigationContext, siblingNavigation],
  );

  const handleTransfer = () => {
    setShowTransferModal(true);
  };

  const handleRequestTransfer = async (newResponsibleId: string, comments?: string) => {
    if (!currentRequest?.id) return;

    await apiService.assignMaintenanceResponsibility(currentRequest.id, newResponsibleId, comments);

    toast.success('Responsabilidade transferida com sucesso!', {
      duration: 3000,
      position: 'top-right',
    });

    // Recarregar a solicitação para mostrar as atualizações
    if (mode.requestId) {
      await loadRequestData(mode.requestId);
    }
  };


  const handleMarkExecutedDirect = async () => {
    if (!currentRequest?.id) return;
    
    try {
      setIsMarkingExecuted(true);
      
      // Validar dados mínimos
      if (!formData.executedAt) {
        setError(isPurchaseRequest ? 'Data da compra é obrigatória' : 'Data de execução é obrigatória');
        return;
      }
      
      // Usar mesma lógica do MarkExecutedModal mas sem modal
      const executedData = {
        targetStatus: 'EXECUTADO' as const,
        executedAt: formData.executedAt.toISOString(),
        notes: formData.solutionDescription?.trim() || undefined, // Usar solutionDescription em vez de executionNotes
        // O custo preenchido no registo é sempre estimatedCost (actualCost é definido pelo CFO ao concluir)
        estimatedCost: (() => { const v = parseFloat(String(formData.estimatedCost ?? '')); return isNaN(v) ? undefined : v; })(),
        partnerNotes: formData.partnerNotes?.trim() || undefined, // Adicionar partnerNotes
        rating: formData.rating || undefined, // Adicionar rating também para execução
        finalObservations: formData.finalObservations?.trim() || undefined, // Adicionar finalObservations
        solutionDescription: formData.solutionDescription?.trim() || undefined, // Adicionar solutionDescription como campo separado
        responsibleTechnician: formData.responsibleTechnician?.trim() || undefined // Adicionar responsibleTechnician
      };
      
      // Upload de novos anexos antes da transição; backend persiste via PATCH /status
      let newAttachmentLinks: string[] = [];
      let newUploadedFiles: AttachmentInfo[] = [];
      if (formData.attachments?.length) {
        newUploadedFiles = await uploadFiles(currentRequest.id);
        newAttachmentLinks = newUploadedFiles.map(att => att.webViewLink);
      }

      const payload = newAttachmentLinks.length > 0
        ? { ...executedData, attachmentUrls: newAttachmentLinks }
        : executedData;

      await apiService.updateMaintenanceStatus(currentRequest.id, payload);

      // Refletir os anexos recém-subidos no estado local após o sucesso da transição
      if (newUploadedFiles.length > 0) {
        setFormData(prev => ({
          ...prev,
          attachments: [],
          attachmentUrls: [...(prev.attachmentUrls || []), ...newUploadedFiles]
        }));
      }

      // Invalidar cache e atualizar
      invalidateMaintenanceCache('update', currentRequest.id, currentRequest.asset?.store);
      
      toast.success('Requisição marcada como executada com sucesso!');
      
      // Recarregar dados
      await loadRequestData(currentRequest.id);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao marcar como executado');
    } finally {
      setIsMarkingExecuted(false);
    }
  };


  const confirmCompletion = async () => {
    setShowCompletionModal(false);
    // Mesmo motivo de handleSubmit: o CompletionModal chama onConfirm como
    // fire-and-forget, então uma rejection aqui se torna unhandledrejection.
    // processSubmit já fez setError; engolir intencionalmente.
    try {
      await processSubmit();
    } catch {
      // already surfaced via setError; intentional swallow
    }
  };

  const handleSave = async () => {
    if (!currentRequest?.id) return;
    
    // Se há exclusões pendentes, mostrar modal de confirmação
    if (pendingDeletions.length > 0) {
      setShowPendingDeletionsModal(true);
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Upload de novos anexos
      let newUploadedFiles: AttachmentInfo[] = [];
      if (formData.attachments?.length) {
        newUploadedFiles = await uploadFiles(currentRequest.id);
      }
      
      // Preparar payload com apenas os campos visíveis e aceitos pelo backend
      // NOTA: scheduledArrangementDate NÃO é suportado pelo maintenanceRequestUpdateSchema
      const payload: {
        problemDescription: string
        urgency: string
        estimatedCost?: number
        attachmentUrls?: string[]
      } = {
        problemDescription: formData.problemDescription.trim(),
        urgency: formData.urgency,
        estimatedCost: (() => { const v = parseFloat(String(formData.estimatedCost ?? '')); return isNaN(v) ? undefined : v; })(),
      };

      // Combinar anexos existentes com novos
      const allAttachmentUrls = [...(formData.attachmentUrls || []), ...newUploadedFiles].map(att => att.webViewLink);
      payload.attachmentUrls = allAttachmentUrls;
      
      // Atualizar requisição
      const updated = await apiService.updateMaintenanceRequest(currentRequest.id, payload);
      invalidateMaintenanceCache('update', updated.id, updated.asset?.store);
      
      toast.success('Requisição atualizada com sucesso!');
      
      // Recarregar dados (sem redirecionar)
      await loadRequestData(currentRequest.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-700"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            A carregar...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <button
            type="button"
            data-testid="back-to-list-top"
            aria-label="Voltar para a listagem"
            onClick={handleBackToList}
            className="inline-flex items-center px-3 py-1.5 border border-brand-neutral-300 rounded-md shadow-sm text-sm font-medium text-brand-text-primary bg-white hover:bg-brand-surface-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-neutral-500"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </button>
        </div>
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isReadOnlyMode()
                  ? 'Detalhes da Requisição'
                  : mode.type === 'request'
                    ? (mode.isEdit ? 'Editar Requisição' : isPurchaseContext ? 'Nova Requisição de Compra' : 'Nova Requisição de Manutenção')
                    : isPurchaseRequest ? 'Registar Compra' : 'Registar Execução de Manutenção'
                }
              </h1>
              <p className="mt-2 text-gray-600">
                {isReadOnlyMode()
                  ? (isPurchaseRequest ? 'Visualização dos dados da requisição de compra' : 'Visualização dos dados da requisição de manutenção')
                  : mode.type === 'request'
                    ? (isPurchaseContext ? 'Preencha os dados para efetuar uma requisição de compra' : 'Preencha os dados para requisitar manutenção de ativo')
                    : isPurchaseRequest ? 'Registe as informações da compra realizada' : 'Registe as informações da manutenção executada'
                }
              </p>
            </div>
            {currentRequest && (
              <div className="flex flex-col items-end gap-2">
                {/* QA testid: primary header status badge — rendered whenever a request is loaded, in
                    BOTH edit and read-only modes, for every state. This is the ONE live render site for
                    data-testid="request-status"; the inner-section status spans (edit-details and
                    read-only-details blocks) are NOT instrumented to avoid duplicate-testid ambiguity.
                    The badge is visually rendered (not display:none) so a vision tool can crop it to
                    judge colour/icon. data-status carries the canonical enum token. */}
                <span
                  data-testid="request-status"
                  data-status={currentRequest.status}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeClass(currentRequest.status)}`}
                >
                  {(() => {
                    const IconComponent = getStatusIcon(currentRequest.status)
                    return IconComponent ? <IconComponent className="h-3.5 w-3.5" aria-hidden /> : null
                  })()}
                  {getStatusLabel(currentRequest.status, (currentRequest as MaintenanceRequest)?.subcategory)}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  ID: {currentRequest.id?.substring(0, 8)}...
                </span>
                {currentRequest.status === 'APROVADO' && (currentRequest as MaintenanceRequest).approvalDate && (
                  <span className="text-xs text-green-600 mt-1">
                    {getApprovedDatePrefix((currentRequest as MaintenanceRequest).subcategory)}{' '}
                    {new Date((currentRequest as MaintenanceRequest).approvalDate!).toLocaleDateString('pt-PT')}
                  </span>
                )}
                {currentRequest.status === 'REJEITADO' && (currentRequest as MaintenanceRequest).approvalDate && (
                  <span className="text-xs text-red-600 mt-1">
                    Rejeitado em: {new Date((currentRequest as MaintenanceRequest).approvalDate!).toLocaleDateString('pt-PT')}
                  </span>
                )}

                {/* Issue #208 — navegação directa por setas entre requisições
                    dentro do filtro activo. Só renderiza quando temos um
                    contexto de navegação (i.e. chegámos aqui a partir da
                    lista filtrada). Em novos formulários (`/novo`) ou em
                    deep-links sem state, o componente simplesmente não
                    aparece. As setas são desabilitadas nas extremidades
                    para dar feedback visual claro. */}
                {(navigationContext || siblingNavigation) && (
                  <div
                    data-testid="sibling-navigation"
                    className="mt-2 flex items-center gap-2"
                  >
                    <button
                      type="button"
                      onClick={() => navigateToSibling(previousSiblingId)}
                      disabled={!previousSiblingId || siblingNavigationLoading}
                      aria-label="Ir para a requisição anterior no filtro activo"
                      data-testid="sibling-prev-button"
                      title={previousSiblingId ? 'Anterior no filtro activo' : 'Sem requisição anterior'}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden />
                    </button>
                    <span
                      data-testid="sibling-position"
                      className="text-xs font-medium text-gray-600 min-w-[5rem] text-center"
                      aria-live="polite"
                    >
                      {siblingNavigationLoading
                        ? 'A carregar...'
                        : siblingPosition ?? '—'}
                    </span>
                    <button
                      type="button"
                      onClick={() => navigateToSibling(nextSiblingId)}
                      disabled={!nextSiblingId || siblingNavigationLoading}
                      aria-label="Ir para a próxima requisição no filtro activo"
                      data-testid="sibling-next-button"
                      title={nextSiblingId ? 'Seguinte no filtro activo' : 'Sem próxima requisição'}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* QA testid: main content region — the outermost visible card wrapping the entire detail
            body (error banner + detail sections + partner/history/attachments + action footer). It is
            the ONE live render site for data-testid="main-content-region" and is present in every mode
            where a request is shown (edit, read-only, and the APROVADO registration mode). It has a real
            bounding box (bg-white shadow rounded-lg, never display:none / zero-size), so a screenshot of
            it represents "the landing screen" for the no-visual-breakage vision check. */}
        <div data-testid="main-content-region" className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-6">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Erro</h3>
                    <div className="mt-2 text-sm text-red-700">{error}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Request details display in edit mode for non-readonly statuses */}
            {mode.isEdit && currentRequest && isRequest(currentRequest) && !isReadOnlyMode() && (
              <>
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Detalhes da Requisição
                  </h3>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="font-medium text-gray-700">Ativo:</span>
                        <p className="text-gray-900">
                          {currentRequest.equipmentId}
                          {currentRequest.equipmentDescription ? ` - ${currentRequest.equipmentDescription}` :
                           currentRequest.department ? ` - ${currentRequest.department}` : ''}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Status:</span>
                        <p className="text-gray-900">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClass(currentRequest.status)}`}
                          >
                            {(() => {
                              const IconComponent = getStatusIcon(currentRequest.status)
                              return IconComponent ? <IconComponent className="h-3.5 w-3.5" aria-hidden /> : null
                            })()}
                            {getStatusLabel(currentRequest.status, (currentRequest as MaintenanceRequest)?.subcategory)}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Urgência:</span>
                        <p className="text-gray-900">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            currentRequest.urgency === 'baixa' ? 'bg-gray-100 text-gray-800' :
                            currentRequest.urgency === 'media' ? 'bg-yellow-100 text-yellow-800' :
                            currentRequest.urgency === 'alta' ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {currentRequest.urgency === 'baixa' ? 'Baixa' :
                             currentRequest.urgency === 'media' ? 'Média' :
                             currentRequest.urgency === 'alta' ? 'Alta' :
                             'Crítica'}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Data da Requisição:</span>
                        <p className="text-gray-900">{currentRequest.requestDate ? new Date(currentRequest.requestDate).toLocaleDateString('pt-PT') : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Custo Estimado:</span>
                        <p className="text-gray-900">€{(currentRequest.estimatedCost ?? 0).toFixed(2)}</p>
                      </div>
                      {currentRequest.approverId && (
                        <div>
                          <span className="font-medium text-gray-700">Data de Aprovação:</span>
                          <p className="text-gray-900">{currentRequest.approvalDate ? new Date(currentRequest.approvalDate).toLocaleDateString('pt-PT') : 'N/A'}</p>
                        </div>
                      )}
                      {/* QA testid: problem-description display (edit mode). Mutually exclusive with the
                          read-only and registration display blocks, so only ONE data-testid="problemDescription"
                          is live at a time. MF-B masks this region from the vision screenshot. */}
                      <div data-testid="problemDescription" className="md:col-span-2">
                        <span className="font-medium text-gray-700">Descrição do Problema:</span>
                        <p className="text-gray-900 mt-1">{currentRequest.problemDescription}</p>
                      </div>
                      {currentRequest.approvalComments && (
                        <div className="md:col-span-2">
                          <span className="font-medium text-gray-700">{currentRequest.status === 'REJEITADO' ? 'Motivo da Rejeição:' : 'Comentários da Aprovação:'}</span>
                          <p className="text-gray-900 mt-1">{currentRequest.approvalComments}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Equipment information */}
                  {getSelectedAsset() && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-md font-medium text-gray-900">Informações do Ativo</h4>
                        <AssetStatusBadge status={getSelectedAsset()?.status} />
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="font-medium text-gray-700">Nome do Ativo:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.description || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Valor Comercial:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.commercialValue ? `€${Number(getSelectedAsset()?.commercialValue).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}` : 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Departamento:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.department || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Nível de Aprovação:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.approvalLevel || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Garantia:</span>
                            <div className="mt-1">
                              <WarrantyBadge warrantyExpiryDate={getSelectedAsset()?.warrantyExpiryDate} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Request details display in read-only mode (requisicao AND registo) */}
            {isReadOnlyMode() && currentRequest && (
              <>
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Detalhes da Requisição
                  </h3>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="font-medium text-gray-700">Ativo:</span>
                        <p className="text-gray-900">
                          {currentRequest.equipmentId}
                          {currentRequest.equipmentDescription ? ` - ${currentRequest.equipmentDescription}` :
                           (currentRequest as MaintenanceRequest).department ? ` - ${(currentRequest as MaintenanceRequest).department}` : ''}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Status:</span>
                        <p className="text-gray-900">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClass(currentRequest.status)}`}
                          >
                            {(() => {
                              const IconComponent = getStatusIcon(currentRequest.status)
                              return IconComponent ? <IconComponent className="h-3.5 w-3.5" aria-hidden /> : null
                            })()}
                            {getStatusLabel(currentRequest.status, (currentRequest as MaintenanceRequest)?.subcategory)}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Urgência:</span>
                        <p className="text-gray-900">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            (currentRequest as MaintenanceRequest).urgency === 'baixa' ? 'bg-gray-100 text-gray-800' :
                            (currentRequest as MaintenanceRequest).urgency === 'media' ? 'bg-yellow-100 text-yellow-800' :
                            (currentRequest as MaintenanceRequest).urgency === 'alta' ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {(currentRequest as MaintenanceRequest).urgency === 'baixa' ? 'Baixa' :
                             (currentRequest as MaintenanceRequest).urgency === 'media' ? 'Média' :
                             (currentRequest as MaintenanceRequest).urgency === 'alta' ? 'Alta' :
                             'Crítica'}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Data da Requisição:</span>
                        <p className="text-gray-900">{currentRequest.requestDate ? new Date(currentRequest.requestDate).toLocaleDateString('pt-PT') : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Custo Estimado:</span>
                        <p className="text-gray-900">€{((currentRequest as MaintenanceRequest).estimatedCost ?? 0).toFixed(2)}</p>
                      </div>
                      {(currentRequest as MaintenanceRequest).approverId && (
                        <div>
                          <span className="font-medium text-gray-700">Data de Aprovação:</span>
                          <p className="text-gray-900">{(currentRequest as MaintenanceRequest).approvalDate ? new Date((currentRequest as MaintenanceRequest).approvalDate!).toLocaleDateString('pt-PT') : 'N/A'}</p>
                        </div>
                      )}
                      {/* QA testid: problem-description display (read-only mode). Mutually exclusive with
                          the edit and registration display blocks, so only ONE data-testid="problemDescription"
                          is live at a time. MF-B masks this region from the vision screenshot. */}
                      <div data-testid="problemDescription" className="md:col-span-2">
                        <span className="font-medium text-gray-700">Descrição do Problema:</span>
                        <p className="text-gray-900 mt-1">{(currentRequest as MaintenanceRequest).problemDescription}</p>
                      </div>
                      {(currentRequest as MaintenanceRequest).approvalComments && (
                        <div className="md:col-span-2">
                          <span className="font-medium text-gray-700">{currentRequest.status === 'REJEITADO' ? 'Motivo da Rejeição:' : 'Comentários da Aprovação:'}</span>
                          <p className="text-gray-900 mt-1">{(currentRequest as MaintenanceRequest).approvalComments}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Execution data (for EXECUTADO/CONCLUIDO requests) */}
                  {(currentRequest.status === 'EXECUTADO' || currentRequest.status === 'CONCLUIDO') && (
                    <div className="mt-4 bg-green-50 rounded-lg p-4">
                      <h4 className="text-md font-medium text-gray-900 mb-3">{isPurchaseRequest ? 'Dados da Compra' : 'Dados da Execução'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {currentRequest.executedAt && (
                          <div>
                            <span className="font-medium text-gray-700">{isPurchaseRequest ? 'Data da Compra:' : 'Data de Execução:'}</span>
                            <p className="text-gray-900">{new Date(currentRequest.executedAt).toLocaleDateString('pt-PT')}</p>
                          </div>
                        )}
                        {currentRequest.actualCost != null && currentRequest.status === 'CONCLUIDO' && (
                          <div>
                            <span className="font-medium text-gray-700">Custo Real (CFO):</span>
                            <p className="text-gray-900 font-semibold">€{Number(currentRequest.actualCost).toFixed(2)}</p>
                          </div>
                        )}
                        {(currentRequest as MaintenanceRequest).solutionDescription && (
                          <div className="md:col-span-2">
                            <span className="font-medium text-gray-700">{isPurchaseRequest ? 'Descrição da Compra:' : 'Descrição da Solução:'}</span>
                            <p className="text-gray-900 mt-1">{(currentRequest as MaintenanceRequest).solutionDescription}</p>
                          </div>
                        )}
                        {(currentRequest as MaintenanceRequest).responsibleTechnician && (
                          <div>
                            <span className="font-medium text-gray-700">Técnico Responsável:</span>
                            <p className="text-gray-900">
                              {(() => {
                                const techId = (currentRequest as MaintenanceRequest).responsibleTechnician;
                                const partner = currentRequest.partners?.find(p => p.partnerId === techId);
                                return partner ? partner.name : techId;
                              })()}
                            </p>
                          </div>
                        )}
                        {(currentRequest as MaintenanceRequest).partnerId && (
                          <div>
                            <span className="font-medium text-gray-700">Parceiro:</span>
                            <p className="text-gray-900">
                              {(() => {
                                const pid = (currentRequest as MaintenanceRequest).partnerId;
                                const partner = currentRequest.partners?.find(p => p.partnerId === pid);
                                return partner ? partner.name : pid;
                              })()}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Equipment information */}
                  {getSelectedAsset() && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-md font-medium text-gray-900">Informações do Ativo</h4>
                        <AssetStatusBadge status={getSelectedAsset()?.status} />
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="font-medium text-gray-700">Nome do Ativo:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.description || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Valor Comercial:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.commercialValue ? `€${Number(getSelectedAsset()?.commercialValue).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}` : 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Departamento:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.department || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Nível de Aprovação:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.approvalLevel || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Garantia:</span>
                            <div className="mt-1">
                              <WarrantyBadge warrantyExpiryDate={getSelectedAsset()?.warrantyExpiryDate} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Request details display in registration mode (apenas enquanto APROVADO, antes de executar) */}
            {mode.type === 'registration' && !isReadOnlyMode() && isRequest(currentRequest) && (
              <>
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Detalhes da Requisição
                    </h3>
                    {mode.type === 'registration' && (
                      <button
                        type="button"
                        onClick={handleSwitchToEditMode}
                        className="p-2 rounded-md bg-white shadow-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        title="Editar requisição"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="font-medium text-gray-700">Ativo:</span>
                        <p className="text-gray-900">
                          {currentRequest.equipmentId}
                          {currentRequest.equipmentDescription ? ` - ${currentRequest.equipmentDescription}` : currentRequest.department ? ` - ${currentRequest.department}` : ''}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Status:</span>
                        <p className="text-gray-900">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClass(currentRequest.status)}`}
                          >
                            {(() => {
                              const IconComponent = getStatusIcon(currentRequest.status)
                              return IconComponent ? <IconComponent className="h-3.5 w-3.5" aria-hidden /> : null
                            })()}
                            {getStatusLabel(currentRequest.status, (currentRequest as MaintenanceRequest)?.subcategory)}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Urgência:</span>
                        <p className="text-gray-900">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            currentRequest.urgency === 'baixa' ? 'bg-gray-100 text-gray-800' :
                            currentRequest.urgency === 'media' ? 'bg-yellow-100 text-yellow-800' :
                            currentRequest.urgency === 'alta' ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {currentRequest.urgency === 'baixa' ? 'Baixa' :
                             currentRequest.urgency === 'media' ? 'Média' :
                             currentRequest.urgency === 'alta' ? 'Alta' :
                             'Crítica'}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Data da Requisição:</span>
                        <p className="text-gray-900">{currentRequest.requestDate ? new Date(currentRequest.requestDate).toLocaleDateString('pt-PT') : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Custo Estimado:</span>
                        <p className="text-gray-900">€{(currentRequest.estimatedCost ?? 0).toFixed(2)}</p>
                      </div>
                      {currentRequest.approverId && (
                        <div>
                          <span className="font-medium text-gray-700">Data de Aprovação:</span>
                          <p className="text-gray-900">{currentRequest.approvalDate ? new Date(currentRequest.approvalDate).toLocaleDateString('pt-PT') : 'N/A'}</p>
                        </div>
                      )}
                      {/* QA testid: problem-description display (registration mode, APROVADO). Mutually
                          exclusive with the edit and read-only display blocks, so only ONE
                          data-testid="problemDescription" is live at a time. MF-B masks this region. */}
                      <div data-testid="problemDescription" className="md:col-span-2">
                        <span className="font-medium text-gray-700">Descrição do Problema:</span>
                        <p className="text-gray-900 mt-1">{currentRequest.problemDescription}</p>
                      </div>
                      {currentRequest.approvalComments && (
                        <div className="md:col-span-2">
                          <span className="font-medium text-gray-700">{currentRequest.status === 'REJEITADO' ? 'Motivo da Rejeição:' : 'Comentários da Aprovação:'}</span>
                          <p className="text-gray-900 mt-1">{currentRequest.approvalComments}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Equipment information */}
                  {getSelectedAsset() && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-md font-medium text-gray-900">Informações do Ativo</h4>
                        <AssetStatusBadge status={getSelectedAsset()?.status} />
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="font-medium text-gray-700">Nome do Ativo:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.description || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Valor Comercial:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.commercialValue ? `€${Number(getSelectedAsset()?.commercialValue).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}` : 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Departamento:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.department || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Nível de Aprovação:</span>
                            <p className="text-gray-900">{getSelectedAsset()?.approvalLevel || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Garantia:</span>
                            <div className="mt-1">
                              <WarrantyBadge warrantyExpiryDate={getSelectedAsset()?.warrantyExpiryDate} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Radio buttons para escolher tipo de registo (apenas na criação) */}
                {!isReadOnlyMode() && ((mode.type === 'request' && !mode.isEdit) || (mode.type === 'registration' && !mode.requestId)) && (
                  <div className="md:col-span-2 bg-blue-50 p-4 rounded-md border border-blue-200">
                    <label className="block text-sm font-medium text-gray-900 mb-3">
                      Tipo de Registo *
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="requestType"
                          value="requisicao"
                          checked={formData.type === 'requisicao'}
                          onChange={() => {
                            // Clear execution-specific fields when switching to requisicao
                            setFormData(prev => ({
                              ...prev,
                              type: 'requisicao',
                              subcategory: null,
                              executedAt: undefined,
                              solutionDescription: undefined,
                              responsibleTechnician: undefined,
                              actualCost: undefined,
                              rating: undefined,
                              finalObservations: undefined
                            }));
                            setMode({ type: 'request' });
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Situação por Resolver</span>
                        <span className="ml-1 text-xs text-gray-500">(problema a resolver)</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="requestType"
                          value="registo"
                          checked={formData.type === 'registo'}
                          onChange={() => {
                            // Clear subcategory when switching to registo (not applicable)
                            setFormData(prev => ({
                              ...prev,
                              type: 'registo',
                              subcategory: null
                            }));
                            setMode({ type: 'registration' });
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Registo de Execução</span>
                        <span className="ml-1 text-xs text-gray-500">(manutenção já feita)</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Subcategoria: Manutenção vs Compras (apenas para Situação por Resolver na criação) */}
                {!isReadOnlyMode() && formData.type === 'requisicao' && mode.type === 'request' && !mode.isEdit && (
                  <div className="md:col-span-2 bg-brand-neutral-50 p-4 rounded-md border border-brand-neutral-200">
                    <label className="block text-sm font-medium text-gray-900 mb-3">
                      Subcategoria *
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="subcategory"
                          value="manutencao"
                          checked={formData.subcategory === 'manutencao'}
                          onChange={() => setFormData(prev => ({ ...prev, subcategory: 'manutencao' }))}
                          className="h-4 w-4 text-brand-primary-600 focus:ring-brand-primary-500 border-gray-300"
                          required={formData.type === 'requisicao'}
                        />
                        <span className="ml-2 text-sm text-gray-700">Manutenção</span>
                        <span className="ml-1 text-xs text-gray-500">(contactos de parceiros)</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="subcategory"
                          value="compras"
                          checked={formData.subcategory === 'compras'}
                          onChange={() => setFormData(prev => ({ ...prev, subcategory: 'compras' }))}
                          className="h-4 w-4 text-brand-primary-600 focus:ring-brand-primary-500 border-gray-300"
                          required={formData.type === 'requisicao'}
                        />
                        <span className="ml-2 text-sm text-gray-700">Compras</span>
                        <span className="ml-1 text-xs text-gray-500">(exige 3 cotações se todas forem &gt; €200; caso contrário, apenas 1)</span>
                      </label>
                    </div>
                  </div>
                )}

                {!isReadOnlyMode() && ((mode.type === 'request' && !mode.isEdit) || (mode.type === 'registration' && !mode.requestId)) && (
                  <div className="md:col-span-2">
                    <label htmlFor="equipmentId" className="block text-sm font-medium text-gray-700">
                      Ativo *
                    </label>
                    <SearchableSelect
                      options={(assets || []).map((asset) => ({
                        value: asset.equipmentId,
                        label: asset.equipmentId,
                        subtitle: asset.description
                      }))}
                      value={formData.equipmentId}
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, equipmentId: value }));
                      }}
                      placeholder="Selecione um ativo"
                      required
                      name="equipmentId"
                      emptyMessage="Nenhum ativo encontrado"
                      className="mt-1"
                    />
                  </div>
                )}

                {!isReadOnlyMode() && mode.type === 'request' && !mode.isEdit && getSelectedAsset() && (
                  <div className="md:col-span-2 bg-gray-50 p-4 rounded-md">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-900">Informações do Ativo</h3>
                      <AssetStatusBadge status={getSelectedAsset()?.status} size="sm" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Nome do Ativo:</span> {getSelectedAsset()?.description || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Valor Comercial:</span> {getSelectedAsset()?.commercialValue ? `€${Number(getSelectedAsset()?.commercialValue).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}` : 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Departamento:</span> {getSelectedAsset()?.department || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Nível de Aprovação:</span> {getSelectedAsset()?.approvalLevel || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Garantia:</span> <WarrantyBadge warrantyExpiryDate={getSelectedAsset()?.warrantyExpiryDate} size="sm" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Informações de Aprovação Automática (read-only, apenas na edição — não no registo de execução) */}
                {isRequest(currentRequest) && (currentRequest as MaintenanceRequest).autoApprovalSource && mode.type !== 'registration' && !isPurchaseRequest && (
                  <div className="md:col-span-2 bg-blue-50 p-4 rounded-md border border-blue-200">
                    <h3 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Configuração de Aprovação
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-blue-700">Modo de Aprovação:</span>
                        <p className="text-blue-600">
                          {(currentRequest as MaintenanceRequest).autoApprovalSource === 'ADMIN_ONLY'
                            ? 'Apenas Administrador'
                            : 'Gerente com Limite'}
                        </p>
                      </div>
                      {(currentRequest as MaintenanceRequest).autoApprovalThreshold && (
                        <div>
                          <span className="font-medium text-blue-700">Limite de Aprovação:</span>
                          <p className="text-blue-600">
                            €{(currentRequest as MaintenanceRequest).autoApprovalThreshold?.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                

                {mode.type === 'request' && !isReadOnlyMode() && (
                  <>
                    {/* Urgência: oculta em compras em fase pré-registo (irrelevante)
                        e em manutenção em triagem (prematuro) */}
                    {!shouldHideMaintenanceFields && (
                      <div>
                        <label htmlFor="urgency" className="block text-sm font-medium text-gray-700">
                          Urgência *
                        </label>
                        <StyledSelect
                          options={[
                            { value: 'baixa', label: 'Baixa', icon: '🟢' },
                            { value: 'media', label: 'Média', icon: '🟡' },
                            { value: 'alta', label: 'Alta', icon: '🟠' },
                            { value: 'critica', label: 'Crítica', icon: '🔴' }
                          ]}
                          value={formData.urgency}
                          onChange={(value) => setFormData(prev => ({ ...prev, urgency: value as 'baixa' | 'media' | 'alta' | 'critica' }))}
                          placeholder="Selecione a urgência"
                          required
                          name="urgency"
                          className="mt-1"
                        />
                      </div>
                    )}

                    {/* Campos adicionais apenas para modo de edição */}
                    {mode.isEdit && currentRequest?.status && !shouldHideMaintenanceFields && (
                        <>
                        <div>
                          <label htmlFor="estimatedCost" className="block text-sm font-medium text-gray-700">
                          Custo Estimado (€)
                          </label>
                          <input
                          type="text"
                          inputMode="decimal"
                          id="estimatedCost"
                          name="estimatedCost"
                          placeholder="0.00"
                          className="mt-1 block w-full px-3 py-2 text-base border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          value={formData.estimatedCost ?? ''}
                          onChange={handleChange}
                          />
                        </div>

                        <div>
                          <label htmlFor="scheduledArrangementDate" className="block text-sm font-medium text-gray-700">
                          Data Prevista para Agendamento
                          </label>
                          <input
                          type="date"
                          id="scheduledArrangementDate"
                          name="scheduledArrangementDate"
                          min={new Date().toISOString().split('T')[0]} // Permitir apenas datas futuras
                          className={`mt-1 block w-full px-3 py-2 text-base border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                            formData.recurrenceSuggestion?.hasConflict
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-300'
                          }`}
                          value={formData.scheduledArrangementDate?.toISOString().split('T')[0] || ''}
                          onChange={handleDateChange}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                          Data prevista para o agendamento da manutenção. Deve ser uma data futura.
                          </p>
                        </div>

                        {/* Recurrence Suggestion Panel — defensivo: garantir invisibilidade em compras */}
                        {formData.recurrenceSuggestion && (
                          <div className="md:col-span-2">
                            <div className={`rounded-lg p-4 ${
                              formData.recurrenceSuggestion.hasConflict
                                ? 'bg-amber-50 border border-amber-200'
                                : 'bg-blue-50 border border-blue-200'
                            }`}>
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                  {formData.recurrenceSuggestion.hasConflict ? (
                                    <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-sm font-medium text-gray-900">
                                    Manutenção Recorrente
                                  </h3>
                                  <div className="mt-2 text-sm text-gray-600">
                                    <p>
                                      <span className="font-medium">Periodicidade:</span> {formData.recurrenceSuggestion.recurrence.recurrenceString}
                                    </p>
                                    {formData.recurrenceSuggestion.recurrence.lastExecutionDate && (
                                      <p>
                                        <span className="font-medium">Última execução:</span> {formatDate(formData.recurrenceSuggestion.recurrence.lastExecutionDate)}
                                      </p>
                                    )}
                                    {formData.recurrenceSuggestion.recurrence.nextDueDate && (
                                      <p>
                                        <span className="font-medium">Próxima data sugerida:</span> {formatDate(formData.recurrenceSuggestion.recurrence.nextDueDate)}
                                      </p>
                                    )}
                                    {formData.recurrenceSuggestion.hasConflict && formData.recurrenceSuggestion.conflictMessage && (
                                      <p className="mt-2 text-amber-700 font-medium">
                                        {formData.recurrenceSuggestion.conflictMessage}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Override Reason - Show when there's a conflict — defensivo em compras */}
                              {formData.recurrenceSuggestion.hasConflict && (
                                <div className="mt-4 pt-4 border-t border-amber-200">
                                  <label htmlFor="overrideReason" className="block text-sm font-medium text-gray-700">
                                    Motivo da Sobreposição Manual *
                                  </label>
                                  <textarea
                                    id="overrideReason"
                                    name="overrideReason"
                                    rows={3}
                                    minLength={15}
                                    required={formData.recurrenceSuggestion.hasConflict}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholder="Descreva o motivo para escolher uma data diferente da sugerida (mínimo 15 caracteres)..."
                                    value={formData.overrideReason || ''}
                                    onChange={(e) => {
                                      setFormData(prev => ({
                                        ...prev,
                                        overrideReason: e.target.value,
                                      }));

                                      // Track metrics when user starts typing override reason
                                      if (e.target.value.length >= 15) {
                                        trackRecurrenceMetric('override_reason_filled', {
                                          equipmentId: formData.equipmentId,
                                          suggestedDate: formData.recurrenceSuggestion?.recurrence.nextDueDate,
                                          selectedDate: formData.scheduledArrangementDate?.toISOString().split('T')[0],
                                          reasonLength: e.target.value.length,
                                        });
                                      }
                                    }}
                                  />
                                  <p className="mt-1 text-xs text-gray-500">
                                    Justificativa necessária quando a data selecionada difere da sugerida pelo sistema.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Loading state for recurrence */}
                        {loadingRecurrence && (
                          <div className="md:col-span-2">
                            <div className="bg-gray-50 rounded-lg p-4">
                              <div className="flex items-center space-x-3">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500"></div>
                                <p className="text-sm text-gray-600">Verificando informações de recorrência...</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Error state for recurrence */}
                        {recurrenceError && (
                          <div className="md:col-span-2">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <div className="flex items-start space-x-3">
                                <svg className="h-5 w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <div className="flex-1">
                                  <p className="text-sm text-red-800">{recurrenceError}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {currentRequest?.status && !['PENDENTE'].includes(currentRequest.status.toString().toUpperCase()) && mode.type !== 'request' && (
                          <div className="md:col-span-2">
                          <label htmlFor="executionNotes" className="block text-sm font-medium text-gray-700">
                            {isPurchaseRequest ? 'Observações da Compra' : 'Observações da Execução'}
                          </label>
                          <textarea
                            id="executionNotes"
                            name="executionNotes"
                            rows={3}
                            minLength={10}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder={isPurchaseRequest ? 'Observações sobre a compra (mínimo 10 caracteres se preenchido)...' : 'Observações sobre a execução da manutenção (mínimo 10 caracteres se preenchido)...'}
                            value={formData.executionNotes}
                            onChange={handleChange}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {isPurchaseRequest ? 'Informações adicionais sobre a compra. Opcional, mas se preenchido deve ter pelo menos 10 caracteres.' : 'Informações adicionais sobre a execução da manutenção. Opcional, mas se preenchido deve ter pelo menos 10 caracteres.'}
                          </p>
                          </div>
                        )}
                        </>
                    )}

                    {/* Descrição do Problema: já exibida em modo read-only no topo do
                        ecrã em compras, portanto escondemos o textarea editável
                        durante a edição pré-registo de uma compra (ou manutenção em
                        triagem) para evitar duplicação. */}
                    {!shouldHideMaintenanceFields && (
                      <div className="md:col-span-2">
                        <label htmlFor="problemDescription" className="block text-sm font-medium text-gray-700">
                          Descrição do Problema *
                        </label>
                        <textarea
                          id="problemDescription"
                          name="problemDescription"
                          required
                          rows={4}
                          minLength={10}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          placeholder="Descreva detalhadamente o problema encontrado..."
                          value={formData.problemDescription}
                          onChange={handleChange}
                        />
                      </div>
                    )}

                  </>
                )}

                {/* Partner Information Panel - Visível em todos os modos (request e registration) */}
                {shouldShowPartnerPanel() && (
                  <div className="md:col-span-2">
                    <PartnerInfoPanel
                      partners={getSelectedAssetPartners()}
                      assetId={formData.equipmentId}
                      quotations={canViewQuotations() && isRequest(currentRequest) ? (currentRequest as MaintenanceRequest).quotations : undefined}
                      selectedQuotationId={canViewQuotations() && isRequest(currentRequest) ? (currentRequest as MaintenanceRequest).selectedQuotationId : undefined}
                      quotationsFulfilled={canViewQuotations() && isRequest(currentRequest) ? (currentRequest as MaintenanceRequest).quotationsFulfilled : undefined}
                      hidePartnerCarousel={shouldHidePartnerCarousel}
                    />
                  </div>
                )}

                {/* Status History Panel - visible when editing/viewing an existing request */}
                {currentRequest && isRequest(currentRequest) && Array.isArray((currentRequest as MaintenanceRequest).statusHistory) && (currentRequest as MaintenanceRequest).statusHistory.length > 0 && (
                  <div className="md:col-span-2">
                    <div className="border-t border-gray-200 pt-4">
                      <h3 className="mb-3 text-sm font-medium text-gray-700">Histórico de Estados</h3>
                      <StatusHistoryPanel
                        entries={(currentRequest as MaintenanceRequest).statusHistory}
                        subcategory={(currentRequest as MaintenanceRequest).subcategory}
                      />
                    </div>
                  </div>
                )}

                {/* Completion data display for completed requests in read-only mode */}
                {isReadOnlyMode() && currentRequest?.status === 'concluido' && hasCompletionData(currentRequest) && (
                  <>
                    <div className="col-span-1 md:col-span-2 border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-medium text-gray-900">
                        Dados da Conclusão
                      </h3>
                    </div>

                    <div className="md:col-span-2 bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="font-medium text-gray-700">{isPurchaseRequest ? 'Data da Compra:' : 'Data de Execução:'}</span>
                          <p className="text-gray-900">{currentRequest.executedAt ? new Date(currentRequest.executedAt).toLocaleDateString('pt-PT') : 'N/A'}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">{isPurchaseRequest ? 'Valor da Compra:' : 'Custo Real:'}</span>
                          <p className="text-gray-900">{currentRequest.actualCost ? `€${currentRequest.actualCost.toFixed(2)}` : 'N/A'}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Técnico Responsável:</span>
                          <p className="text-gray-900">{currentRequest.responsibleTechnician || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Data de Conclusão:</span>
                          <p className="text-gray-900">{currentRequest.completionDate ? new Date(currentRequest.completionDate).toLocaleDateString('pt-PT') : 'N/A'}</p>
                        </div>
                        <div className="md:col-span-2">
                          <span className="font-medium text-gray-700">{isPurchaseRequest ? 'Descrição da Compra:' : 'Descrição da Solução:'}</span>
                          <p className="text-gray-900 mt-1">{currentRequest.solutionDescription || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Avaliação:</span>
                          <div className="flex items-center mt-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <svg
                                key={star}
                                className={`w-5 h-5 ${
                                  currentRequest.rating && star <= currentRequest.rating
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                            <span className="ml-2 text-sm text-gray-600">
                              {currentRequest.rating ? `${currentRequest.rating}/5` : 'Não avaliado'}
                            </span>
                          </div>
                        </div>
                        {currentRequest.finalObservations && (
                          /* QA testid: free-text observations display (read-only, CONCLUIDO). Only renders
                             in read-only mode, mutually exclusive with the registration-mode editable
                             observations textarea, so data-testid="observacoes" stays single-live. MF-B
                             masks this region from the vision screenshot. */
                          <div data-testid="observacoes" className="md:col-span-2">
                            <span className="font-medium text-gray-700">Observações Finais:</span>
                            <p className="text-gray-900 mt-1">{currentRequest.finalObservations}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {mode.type === 'registration' && !isReadOnlyMode() && (
                  <>
                    <div>
                      <label htmlFor="executedAt" className="block text-sm font-medium text-gray-700">
                        {isPurchaseRequest ? 'Data da Compra' : 'Data de Execução'} *
                      </label>
                      <input
                        type="datetime-local"
                        id="executedAt"
                        name="executedAt"
                        required
                        max={new Date().toISOString().slice(0, 16)} // Prevent future dates/times
                        className="mt-1 block w-full px-3 py-2 text-base border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={formData.executedAt ? new Date(formData.executedAt).toISOString().slice(0, 16) : ''}
                        onChange={(e) => {
                          const { name, value } = e.target;
                          setFormData(prev => ({
                            ...prev,
                            [name]: value ? new Date(value) : undefined,
                          }));
                        }}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {isPurchaseRequest ? 'Data e hora em que a compra foi realizada.' : 'Data e hora em que a manutenção foi executada.'}
                      </p>
                    </div>

                    <div>
                      <label htmlFor="estimatedCost" className="block text-sm font-medium text-gray-700">
                        {isPurchaseRequest ? 'Valor da Compra (€)' : 'Custo Estimado (€)'}
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        id="estimatedCost"
                        name="estimatedCost"
                        placeholder="0.00"
                        className="mt-1 block w-full px-3 py-2 text-base border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={formData.estimatedCost ?? ''}
                        onChange={handleChange}
                      />
                      {(() => {
                        const reason = '';

                        if (reason) {
                          return (
                            <div className="mt-2 text-sm text-amber-600 bg-amber-50 rounded-md p-2">
                              <div className="flex">
                                <svg className="flex-shrink-0 h-5 w-5 text-amber-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <div>
                                  <strong>Informação:</strong> {reason}
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>

                    <div className="md:col-span-2">
                      <label htmlFor="solutionDescription" className="block text-sm font-medium text-gray-700">
                        {isPurchaseRequest ? 'Descrição da Compra' : 'Descrição da Solução'} *
                      </label>
                      <textarea
                        id="solutionDescription"
                        name="solutionDescription"
                        required
                        rows={4}
                        minLength={20}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder={isPurchaseRequest ? 'Descreva os detalhes da compra realizada...' : 'Descreva a solução aplicada e as ações realizadas...'}
                        value={formData.solutionDescription}
                        onChange={handleChange}
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        {isPurchaseRequest ? 'Mínimo de 20 caracteres obrigatório para registar a compra' : 'Mínimo de 20 caracteres obrigatório para concluir a manutenção'}
                      </p>
                    </div>

                    <div>
                      <label htmlFor="responsibleTechnician" className="block text-sm font-medium text-gray-700">
                        Parceiro Responsável *
                      </label>
                      <select
                        id="responsibleTechnician"
                        name="responsibleTechnician"
                        required
                        disabled={isReadOnlyMode()}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md disabled:bg-gray-100"
                        value={formData.responsibleTechnician}
                        onChange={handleChange}
                      >
                        <option value="">Selecione um parceiro</option>
                        {getSelectedAssetPartners().map((partner) => (
                          <option key={partner.partnerId} value={partner.partnerId}>
                            {partner.name} - {partner.specialization?.split(';')[0]?.trim() || 'Sem especialização'}
                          </option>
                        ))}
                        <option value="Técnico Externo">Técnico Externo (especificar nos comentários)</option>
                      </select>
                      {getSelectedAssetPartners().length === 0 && (
                        <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded-md">
                          <p className="text-sm text-amber-800">
                            <strong>Atenção:</strong> Nenhum parceiro atribuído a este equipamento.
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            Selecione &quot;T&eacute;cnico Externo&quot; e especifique nos coment&aacute;rios finais quem realizou a manuten&ccedil;&atilde;o.
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Avalia&ccedil;&atilde;o (1-5)
                      </label>
                      <div className="flex items-center space-x-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            disabled={isReadOnlyMode()}
                            onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                            className={`w-8 h-8 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors ${
                              isReadOnlyMode() ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110 transform transition-transform'
                            }`}
                          >
                            <svg
                              className={`w-8 h-8 ${
                                formData.rating && star <= formData.rating
                                  ? 'text-yellow-400 fill-current'
                                  : 'text-gray-300'
                              }`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          </button>
                        ))}
                        <span className="ml-2 text-sm text-gray-600">
                          {formData.rating ? `${formData.rating}/5` : 'Não avaliado'}
                        </span>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label htmlFor="finalObservations" className="block text-sm font-medium text-gray-700">
                        Observações Finais
                      </label>
                      <textarea
                        id="finalObservations"
                        name="finalObservations"
                        rows={3}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder={isPurchaseRequest ? 'Quaisquer observações adicionais sobre a compra...' : 'Quaisquer observações adicionais sobre a manutenção...'}
                        value={formData.finalObservations}
                        onChange={handleChange}
                      />
                    </div>
                  </>
                )}

                {/* Indicador de exclusões pendentes - adicionar antes da seção de anexos */}
                {pendingDeletions.length > 0 && (
                  <div className="md:col-span-2 bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-red-800">
                          Exclusões Pendentes
                        </h3>
                        <div className="mt-1 text-sm text-red-700">
                          <p>{pendingDeletions.length} anexo(s) marcado(s) para exclusão.</p>
                          <p>Clique em &quot;Guardar&quot; para aplicar as altera&ccedil;&otilde;es.</p>
                        </div>
                      </div>
                      <button
                        onClick={cancelAllPendingDeletions}
                        className="text-sm text-red-600 hover:text-red-800 underline"
                      >
                        Cancelar exclus&otilde;es
                      </button>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs text-red-600">Arquivos que serão excluídos:</p>
                      <ul className="list-disc list-inside mt-1 text-xs text-red-600">
                        {pendingDeletions.map((deletion, index) => (
                          <li key={index}>{deletion.name}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700">
                    Anexos (Imagens, Vídeos, PDF)
                  </label>
                  {!isReadOnlyMode() && (
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-4.5V12.75a4.5 4.5 0 00-4.5-4.5H15.5"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex justify-center text-sm text-brand-text-secondary">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                          >
                            <span>Carregar um ficheiro</span>
                            <input 
                              id="file-upload" 
                              name="file-upload" 
                              type="file" 
                              className="sr-only" 
                              multiple 
                              onChange={handleFileSelect} 
                            />
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">JPG, PNG, GIF, MP4, MOV, PDF até 10MB</p>
                      </div>
                    </div>
                  )}
                  <div className="mt-4">
                    {/* Display existing attachments from Google Drive */}
                    {formData.attachmentUrls && formData.attachmentUrls.length > 0 ? (
                      formData.attachmentUrls.map((attachment, index) => (
                        <div key={`existing-${attachment.id || `attachment-${index}`}`} className="flex items-center justify-between mt-2 p-2 border rounded-md bg-gray-50">
                        <span className="text-sm text-gray-700">{attachment.name || `Anexo ${index + 1}`}</span>
                        <div className="flex items-center space-x-2">
                          <a 
                            href={attachment.webViewLink} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="Visualizar no Drive"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </a>
                          {!isReadOnlyMode() && (
                            <button
                              type="button"
                              onClick={() => removeAttachment(index, true)} // Indicate it's an existing file
                              className="text-red-600 hover:text-red-800 transition-colors"
                              title="Remover anexo"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                    ) : (
                      isReadOnlyMode() && (
                        <div className="mt-2 text-sm text-gray-500 bg-gray-50 rounded-md p-3 text-center">
                          Nenhum anexo foi enviado para esta requisição.
                        </div>
                      )
                    )}
                    {/* Display new attachments selected for upload */}
                    {!isReadOnlyMode() && attachmentPreviews.filter(item => item.file).map((item, index) => (
                      <div key={`new-${index}`} className="flex items-center justify-between mt-2 p-2 border rounded-md">
                        <div className="flex items-center">
                          {item.preview && item.file?.type.startsWith('image/') && (
                            <img src={item.preview} alt="Preview" className="h-8 w-8 object-cover rounded-md mr-2" />
                          )}
                          <span className="text-sm text-gray-700">{item.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {uploadProgress[item.name] !== undefined && uploadingFiles && (
                            <span className="text-sm text-blue-500">{uploadProgress[item.name]}%</span>
                          )}
                          {item.preview && (
                            <a 
                              href={item.preview} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="Visualizar anexo"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Remover anexo"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                    {uploadingFiles && (
                      <div className="flex items-center mt-4 text-brand-primary-500 text-sm">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-brand-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        A carregar ficheiros...
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Updates Section */}
              <div className="border-t border-brand-neutral-200 pt-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  {/* Header com toggle */}
                  <button
                    type="button"
                    onClick={() => setShowUpdates(!showUpdates)}
                    className="flex items-center gap-2 text-sm font-medium text-brand-text-primary hover:text-brand-primary-600 transition-colors"
                    aria-expanded={showUpdates}
                    aria-controls="updates-list"
                  >
                    <MessageSquare size={18} className="text-brand-primary-500" />
                    <span>Histórico de Updates</span>
                    <span className="text-brand-text-muted">
                      ({currentRequest?.updates?.length || 0})
                    </span>
                    {showUpdates ? (
                      <ChevronUp size={16} className="text-brand-text-muted" />
                    ) : (
                      <ChevronDown size={16} className="text-brand-text-muted" />
                    )}
                  </button>
                  
                  {/* Botão de adicionar update */}
                  {canPostUpdate && currentRequest && (
                    <button
                      type="button"
                      onClick={() => setIsUpdateModalOpen(true)}
                      className="flex items-center gap-1.5 text-sm font-medium text-brand-primary-600 hover:text-brand-primary-700 transition-colors"
                      title="Adicionar novo update"
                    >
                      <Plus size={16} />
                      <span>Novo Update</span>
                    </button>
                  )}
                </div>
                
                {/* Lista de updates (colapsável) */}
                {showUpdates && currentRequest && (
                  <div
                    id="updates-list"
                    className="mt-4 space-y-3 max-h-64 md:max-h-96 overflow-y-auto bg-brand-surface-50 rounded-lg p-4 border border-brand-neutral-200"
                    role="region"
                    aria-label="Lista de updates"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                  >
                    {currentRequest.updates && currentRequest.updates.length > 0 ? (
                      currentRequest.updates.map((update, index) => (
                        <div
                          key={update.id}
                          className={`
                            border-l-4 border-brand-primary-400 pl-4 py-2 bg-white rounded-r-lg
                            ${index === 0 ? 'ring-2 ring-brand-primary-100' : ''}
                          `}
                        >
                          {/* Header do update */}
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm text-brand-text-primary">
                                {update.authorName || 'Utilizador'}
                              </span>
                            </div>
                            <time
                              className="text-xs text-brand-text-muted mr-2"
                              dateTime={new Date(update.timestamp).toISOString()}
                            >
                              {format(new Date(update.timestamp), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                            </time>
                          </div>
                          
                          {/* Mensagem do update */}
                          <p className="text-sm text-brand-text-secondary leading-relaxed whitespace-pre-wrap">
                            {update.message}
                          </p>
                          
                          {/* Badge "Mais recente" */}
                          {index === 0 && (
                            <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-primary-600">
                              <Clock size={12} />
                              <span>Mais recente</span>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-brand-text-muted">
                        <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Nenhum update registrado ainda</p>
                        {canPostUpdate && (
                          <button
                            type="button"
                            onClick={() => setIsUpdateModalOpen(true)}
                            className="mt-2 text-sm text-brand-primary-600 hover:underline"
                          >
                            Adicionar o primeiro update
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div data-testid="action-panel" className="pt-5">
                {/* QA testid: action/decision panel — the footer region holding the state's action
                    controls (triage / approval / rejection / CFO value-confirm / mark-executed / save).
                    Scoped to the action footer only; it does NOT contain the free-text
                    problem-description/notes fields above, so the captured DOM slice stays clean. */}
                {/* Validation banner - full width above buttons */}
                {mode.type === 'registration' && !isReadOnlyMode() && !canSubmit() && (() => {
                  const validationDetails = getCompletionValidationDetails();
                  if (!validationDetails) return null;
                  const invalidChecks = Object.values(validationDetails).filter(check => !check.valid);
                  if (invalidChecks.length === 0) return null;
                  return (
                    <div className="mb-3 p-2.5 bg-yellow-50 border border-yellow-200 rounded-md flex items-center gap-2">
                      <svg className="h-4 w-4 text-yellow-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs text-yellow-700">
                        <span className="font-medium">Pendente:</span>{' '}
                        {invalidChecks.map(c => c.label).join(' · ')}
                      </span>
                    </div>
                  );
                })()}

                <div className="flex flex-col-reverse md:flex-row md:justify-between md:items-center gap-4 relative">
                  {/* Extremo esquerdo: Voltar */}
                  <div className="w-full md:w-auto">
                    <button
                      type="button"
                      data-testid="back-to-list-bottom"
                      aria-label="Voltar para a listagem"
                      onClick={handleBackToList}
                      className="w-full md:w-auto inline-flex items-center justify-center px-4 py-2 border border-brand-neutral-300 rounded-md shadow-sm text-sm font-medium text-brand-text-primary bg-white hover:bg-brand-surface-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-neutral-500"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Voltar
                    </button>
                  </div>
                  
                  
                  {/* Direita: Botões de ação */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-3 w-full md:w-auto">
                    {/* Botão Triagem (para AGUARDANDO_TRIAGEM) */}
                    {currentRequest?.status === 'AGUARDANDO_TRIAGEM' && (currentRequest as MaintenanceRequest).canPerformTriage && isActionAllowed() && (
                      <button
                        type="button"
                        onClick={() => setShowTriageModal(true)}
                        className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      >
                        <Search className="h-4 w-4 mr-2" />
                        Realizar Triagem
                      </button>
                    )}
                    {/* Botões de Cotação (para compras) */}
                    {isRequest(currentRequest) && (currentRequest as MaintenanceRequest).subcategory === 'compras' && (
                      <>
                        {/* HIDE_SEND_QUOTATIONS: mudar `(false as boolean)` para `(true as boolean)` para re-activar */}
                        {/* Enviar emails de cotação (PENDENTE ou APROVADO para compras) */}
                        {(false as boolean) && (currentRequest.status === 'PENDENTE' || currentRequest.status === 'APROVADO') &&
                         (currentRequest as MaintenanceRequest).canManageQuotations &&
                         !(currentRequest as MaintenanceRequest).quotationEmailsSentAt && (
                          <button
                            type="button"
                            onClick={() => setShowSendQuotationsModal(true)}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <Mail className="h-4 w-4 mr-2" />
                            Enviar Cotações
                          </button>
                        )}

                        {/* Adicionar cotação (PENDENTE ou APROVADO para compras) */}
                        {(currentRequest.status === 'PENDENTE' || currentRequest.status === 'APROVADO') &&
                         (currentRequest as MaintenanceRequest).canManageQuotations &&
                         !(currentRequest as MaintenanceRequest).quotationsFulfilled && (
                          <button
                            type="button"
                            onClick={() => setShowAddQuotationModal(true)}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Adicionar Cotação
                          </button>
                        )}

                        {/* Aprovar cotação (CFO/Admin) */}
                        {(user?.type === 'cfo' || user?.type === 'admin') &&
                         (currentRequest as MaintenanceRequest).quotationsFulfilled &&
                         !(currentRequest as MaintenanceRequest).selectedQuotationId && (
                          <button
                            type="button"
                            onClick={() => setShowApproveQuotationModal(true)}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <DollarSign className="h-4 w-4 mr-2" />
                            Aprovar Cotação
                          </button>
                        )}

                        {/* Submeter para Aprovação do CFO (issue #55)
                            - Apenas em PENDENTE
                            - Apenas com quotationsFulfilled === true
                            - Apenas para roles que o backend aceita (gerente/admin/cfo),
                              para evitar 403 FORBIDDEN ao clicar
                            - ESCONDIDO após primeira submissão (cfoNotifiedAt preenchido):
                              o botão deixa de aparecer quando já foi enviado para o CFO
                              porque o pedido de aprovação já está em curso. Caso o
                              utilizador precise de uma nova notificação, este deve
                              contactar o suporte / usar o histórico de atualizações
                              em vez de re-submeter (issue: botão não sumia).
                            - ESCONDIDO quando auto-aprovado por compra de baixo valor
                              (status APROVADO + autoApprovalSource AUTO_PURCHASE_LOW_VALUE)
                            - ESCONDIDO em modo read-only (não-dono staff/supervisor #180) */}
                        {currentRequest.status === 'PENDENTE' &&
                         user && ['gerente', 'admin', 'cfo'].includes(user.type) &&
                         (currentRequest as MaintenanceRequest).quotationsFulfilled &&
                         !(currentRequest as MaintenanceRequest).cfoNotifiedAt &&
                         !isAutoApprovedLowValuePurchase(currentRequest as MaintenanceRequest) &&
                         isActionAllowed() && (
                          <button
                            type="button"
                            onClick={() => setShowSubmitForApprovalModal(true)}
                            data-testid="submit-for-cfo-approval-button"
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Submeter para Aprovação
                          </button>
                        )}

                        {/* Indicador: notificação já enviada ao CFO (issue #102 — botão não sumia)
                            Aparece em vez do botão "Submeter para Aprovação" quando
                            cfoNotifiedAt já está preenchido. Informa o utilizador
                            que o pedido já foi enviado e está a aguardar decisão do CFO. */}
                        {currentRequest.status === 'PENDENTE' &&
                         user && ['gerente', 'admin', 'cfo'].includes(user.type) &&
                         (currentRequest as MaintenanceRequest).quotationsFulfilled &&
                         !!(currentRequest as MaintenanceRequest).cfoNotifiedAt &&
                         !isAutoApprovedLowValuePurchase(currentRequest as MaintenanceRequest) && (
                          <div
                            data-testid="cfo-notification-sent-indicator"
                            className="w-full sm:w-auto flex flex-col items-stretch sm:items-start gap-1"
                          >
                            <span
                              data-testid="cfo-notification-sent-badge"
                              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-purple-200 text-sm font-medium rounded-md text-purple-800 bg-purple-50"
                            >
                              <Mail className="h-4 w-4 mr-2 text-purple-600" />
                              Notificação enviada ao CFO
                            </span>
                            <span className="text-xs text-gray-500 sm:ml-1">
                              {formatCfoNotifiedAt((currentRequest as MaintenanceRequest).cfoNotifiedAt)}
                            </span>
                          </div>
                        )}

                        {/* Badge: compra auto-aprovada por baixo valor
                            (cotação única ≤ AUTO_APPROVE_PURCHASE_THRESHOLD).
                            Substitui o botão "Submeter para Aprovação" — já está APROVADO. */}
                        {isAutoApprovedLowValuePurchase(currentRequest as MaintenanceRequest) && (
                          <div
                            data-testid="auto-approved-low-value-badge"
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-green-200 text-sm font-medium rounded-md text-green-800 bg-green-50"
                          >
                            <BadgeCheck className="h-4 w-4 mr-2 text-green-600" />
                            Aprovada automaticamente (cotação ≤ €{AUTO_APPROVE_PURCHASE_THRESHOLD})
                          </div>
                        )}
                      </>
                    )}

                    {/* Botão CFO Concluir (para EXECUTADO) */}
                    {isReadOnlyMode() && currentRequest?.status === 'EXECUTADO' && canSetActualCost() && (
                      <button
                        type="button"
                        onClick={() => setShowCFOValueConfirmModal(true)}
                        className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Concluir Requisição
                      </button>
                    )}
                    {/* Botões de aprovação/rejeição (fora do registration mode) */}
                    {mode.type !== 'registration' && (
                      <>
                        {/* Rejeitar: disponível para canApprove OU para compras em PENDENTE (funciona como arquivar) */}
                        {(canApprove() || (
                          isRequest(currentRequest) &&
                          (currentRequest as MaintenanceRequest).subcategory === 'compras' &&
                          currentRequest.status === 'PENDENTE' &&
                          user && ['gerente', 'admin'].includes(user.type)
                        )) && isActionAllowed() && (
                          <button
                            type="button"
                            onClick={handleReject}
                            disabled={isProcessingAction}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <X className="h-4 w-4 mr-2" />
                            Rejeitar
                          </button>
                        )}
                        {canApprove() && isActionAllowed() && (
                          <button
                            type="button"
                            onClick={handleApprove}
                            disabled={isProcessingAction}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-primary-500 hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Aprovar
                          </button>
                        )}
                        {canTransfer() && isActionAllowed() && (
                          <button
                            type="button"
                            onClick={handleTransfer}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-accent-500 hover:bg-brand-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent-500"
                          >
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l-4-4" />
                            </svg>
                            Transferir
                          </button>
                        )}
                        {canComplete() && !mode.isEdit && isActionAllowed() && (
                          <button
                            type="button"
                            onClick={handleComplete}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-primary-500 hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500"
                          >
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Concluir
                          </button>
                        )}
                        {mode.type === 'request' && mode.isEdit && currentRequest?.status === 'APROVADO' && isActionAllowed() ? (
                          <button
                            type="button"
                            onClick={handleSwitchToRegistrationMode}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-primary-500 hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500"
                          >
                            <Wrench className="h-4 w-4 mr-2" />
                            Executar/Concluir
                          </button>
                        ) : canMarkExecuted() ? (
                          <button
                            type="button"
                            onClick={handleMarkExecutedDirect}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Wrench className="h-4 w-4 mr-2" />
                            Executado
                          </button>
                        ) : null}
                        {canShowSaveButton() && (
                          <button
                            type="button"
                            onClick={handleSave}
                            disabled={submitting || uploadingFiles}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-primary-500 hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {submitting ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                A guardar...
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4 mr-2" />
                                Guardar
                              </>
                            )}
                          </button>
                        )}
                      </>
                    )}
                    
                    {/* Botão para novas requisições */}
                    {mode.type === 'request' && !mode.isEdit && (
                      <button
                        type="submit"
                        disabled={submitting || uploadingFiles || !canSubmit()}
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brand-primary-500 hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            A criar...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Criar Requisição
                          </>
                        )}
                      </button>
                    )}
                    
                    {/* Botões do registration mode: Executado + Concluído */}
                    {mode.type === 'registration' && !isReadOnlyMode() && (
                      <>
                        {!isReadOnlyMode() && (
                          <button
                            type="submit"
                            disabled={submitting || uploadingFiles || !canSubmit()}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brand-primary-500 hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={!canSubmit() ? 'Preencha todos os campos obrigatórios para concluir' : ''}
                          >
                            {submitting ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                A guardar...
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4 mr-2" />
                                {mode.type === 'registration' ? (isPurchaseRequest ? 'Registar Compra' : 'Registar Execução') : 'Guardar'}
                              </>
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Approval Modal */}
        <ApprovalModal
          isOpen={showApprovalModal}
          onClose={() => setShowApprovalModal(false)}
          onApprove={async (comments?: string) => {
            if (!currentRequest?.id) {
              throw new Error('Dados da requisição não disponíveis. Tente recarregar a página.');
            }
            
            setIsProcessingAction(true);
            try {
              const approved = await apiService.approveMaintenanceRequest(currentRequest.id, {
                comments: comments
              });
              
              // Verificação defensiva para o objeto approved
              if (!approved?.id) {
                throw new Error('Resposta da API de aprovação inválida.');
              }
              
              invalidateMaintenanceCache('approve', approved.id, approved.asset?.store);
              
              toast.success('Requisição aprovada com sucesso!', {
                duration: 3000,
                position: 'top-right',
              });
              
              setShowApprovalModal(false);
              // Reload request data to update status
              await loadRequestData(currentRequest.id);
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Erro ao aprovar');
              throw err; // Deixar o modal tratar o erro
            } finally {
              setIsProcessingAction(false);
            }
          }}
          requestId={currentRequest?.id || ''}
          equipmentDescription={getSelectedAsset()?.description}
          problemDescription={isRequest(currentRequest) ? currentRequest.problemDescription : undefined}
          estimatedCost={isRequest(currentRequest) ? currentRequest.estimatedCost : undefined}
          urgency={isRequest(currentRequest) ? currentRequest.urgency : undefined}
        />

        {/* Rejection Modal */}
        <RejectionModal
          isOpen={showRejectionModal}
          onClose={() => setShowRejectionModal(false)}
          onReject={async (reason: string) => {
            if (!currentRequest) return;
            
            setIsProcessingAction(true);
            try {
              const rejected = await apiService.rejectMaintenanceRequest(currentRequest.id, {
                reason: reason
              });
              invalidateMaintenanceCache('reject', rejected.id, rejected.asset?.store);
              
              toast.success('Requisição rejeitada com sucesso!', {
                duration: 3000,
                position: 'top-right',
              });
              
              setShowRejectionModal(false);
              // Reload request data to update status
              await loadRequestData(currentRequest.id);
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Erro ao rejeitar');
              throw err; // Deixar o modal tratar o erro
            } finally {
              setIsProcessingAction(false);
            }
          }}
          requestId={currentRequest?.id || ''}
          equipmentDescription={getSelectedAsset()?.description}
          problemDescription={isRequest(currentRequest) ? currentRequest.problemDescription : undefined}
          estimatedCost={isRequest(currentRequest) ? currentRequest.estimatedCost : undefined}
          urgency={isRequest(currentRequest) ? currentRequest.urgency : undefined}
        />

        {/* Completion Modal */}
        <CompletionModal
          isOpen={showCompletionModal}
          onClose={() => setShowCompletionModal(false)}
          onConfirm={confirmCompletion}
          requestId={currentRequest?.id || ''}
          equipmentDescription={getSelectedAsset()?.description}
          problemDescription={isRequest(currentRequest) ? currentRequest.problemDescription : undefined}
          estimatedCost={isRequest(currentRequest) ? currentRequest.estimatedCost : undefined}
          actualCost={formData.actualCost ? Number(formData.actualCost) : undefined}
          urgency={isRequest(currentRequest) ? currentRequest.urgency : undefined}
          hasAttachments={(formData.attachments?.length || 0) + (formData.attachmentUrls?.length || 0) > 0}
          isPurchase={isPurchaseRequest}
        />
        
        {/* Modal de Transferência de Responsibilidade */}
        {currentRequest && (
          <TransferResponsibilityModal
            isOpen={showTransferModal}
            onClose={() => setShowTransferModal(false)}
            onRequestTransfer={handleRequestTransfer}
            requestId={currentRequest.id}
            currentResponsibleName={(currentRequest as MaintenanceRequest).responsiblePerson?.name}
            equipmentDescription={(currentRequest as MaintenanceRequest).equipmentDescription}
            storeId={(currentRequest as MaintenanceRequest).storeId}
            departmentId={(currentRequest as MaintenanceRequest).department}
          />
        )}

        {/* MODAL REMOVIDO - Nova abordagem não precisa de confirmação individual
        <Modal
          isOpen={!!attachmentToRemove}
          onClose={cancelRemoveAttachment}
          title="Remover anexo"
          size="sm"
          showCloseButton={false}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Tem certeza que deseja remover o anexo <span className="font-medium text-gray-900">"{attachmentToRemove?.name}"</span>?
            </p>
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
              <strong>Atenção:</strong> Esta ação é irreversível e o arquivo será permanentemente removido.
            </p>
            
            {attachmentRemovalError && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {attachmentRemovalError}
              </div>
            )}
            
            <div className="flex justify-end space-x-3 pt-2">
              <button
                onClick={cancelRemoveAttachment}
                className="px-4 py-2 bg-brand-neutral-200 text-brand-neutral-800 rounded-md hover:bg-brand-neutral-300 focus:outline-none focus:ring-2 focus:ring-brand-neutral-500 disabled:opacity-50"
                disabled={isRemovingAttachment}
              >
                Cancelar
              </button>
              <button
                onClick={confirmRemoveAttachment}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 flex items-center"
                disabled={isRemovingAttachment}
              >
                {isRemovingAttachment && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isRemovingAttachment ? 'Removendo...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </Modal>
        */}

        {/* Attachment Deletion Modal */}
        <AttachmentDeletionModal
          isOpen={showPendingDeletionsModal}
          onClose={() => setShowPendingDeletionsModal(false)}
          onConfirm={confirmPendingDeletionsAndSaveOnly}
          attachments={pendingDeletions.map(deletion => ({
            id: deletion.id,
            name: deletion.name,
            webViewLink: deletion.webViewLink,
            size: deletion.size,
          }))}
        />

        {/* Update Modal */}
        {currentRequest && (
          <UpdateModal
            isOpen={isUpdateModalOpen}
            onClose={() => setIsUpdateModalOpen(false)}
            request={currentRequest as MaintenanceRequest}
            onUpdate={handleUpdateSuccess}
          />
        )}

        {/* CFO Value Confirm Modal */}
        {currentRequest && (
          <CFOValueConfirmModal
            isOpen={showCFOValueConfirmModal}
            onClose={() => setShowCFOValueConfirmModal(false)}
            onConfirm={async (finalValue: number, notes?: string) => {
              await apiService.confirmCFOValue(currentRequest.id, { finalValue, notes });
              toast.success('Requisição concluída com sucesso!', {
                duration: 3000,
                position: 'top-right',
              });
              setShowCFOValueConfirmModal(false);
              await loadRequestData(currentRequest.id);
            }}
            request={currentRequest as MaintenanceRequest}
          />
        )}

        {/* Triage Modal */}
        {currentRequest && (
          <TriageModal
            isOpen={showTriageModal}
            onClose={() => setShowTriageModal(false)}
            onConfirm={async (decision: 'NADA' | 'RESOLVE' | 'EU_RESOLVO' | 'CONVERTER_EM_COMPRA', notes?: string) => {
              await apiService.performTriage(currentRequest.id, { decision, notes });
              toast.success('Triagem realizada com sucesso!', {
                duration: 3000,
                position: 'top-right',
              });
              setShowTriageModal(false);
              await loadRequestData(currentRequest.id);
            }}
            request={currentRequest as MaintenanceRequest}
          />
        )}

        {/* Send Quotations Modal */}
        {currentRequest && isRequest(currentRequest) && (
          <SendQuotationsModal
            isOpen={showSendQuotationsModal}
            onClose={() => setShowSendQuotationsModal(false)}
            onConfirm={async () => {
              setQuotationActionLoading(true);
              try {
                await apiService.sendQuotationEmails(currentRequest.id);
                toast.success('Pedidos de cotação enviados com sucesso!', {
                  duration: 3000,
                  position: 'top-right',
                });
                setShowSendQuotationsModal(false);
                await loadRequestData(currentRequest.id);
              } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Erro ao enviar cotações');
              } finally {
                setQuotationActionLoading(false);
              }
            }}
            request={currentRequest as MaintenanceRequest}
            partners={getSelectedAssetPartners()}
            loading={quotationActionLoading}
          />
        )}

        {/* Add Quotation Modal */}
        <AddQuotationModal
          isOpen={showAddQuotationModal}
          onClose={() => setShowAddQuotationModal(false)}
          onConfirm={async (payload) => {
            if (!currentRequest?.id) return;
            setQuotationActionLoading(true);
            try {
              await apiService.addQuotation(currentRequest.id, payload);
              toast.success('Cotação adicionada com sucesso!', {
                duration: 3000,
                position: 'top-right',
              });
              setShowAddQuotationModal(false);
              await loadRequestData(currentRequest.id);
            } catch (err: unknown) {
              const apiErr = err as { error?: string; message?: string };
              const message = apiErr?.error || apiErr?.message || (err instanceof Error ? err.message : 'Erro ao adicionar cotação.');
              throw new Error(message);
            } finally {
              setQuotationActionLoading(false);
            }
          }}
          partners={getSelectedAssetPartners()}
          loading={quotationActionLoading}
        />

        {/* Approve Quotation Modal (CFO) */}
        {currentRequest && isRequest(currentRequest) && (currentRequest as MaintenanceRequest).quotations && (
          <ApproveQuotationModal
            isOpen={showApproveQuotationModal}
            onClose={() => setShowApproveQuotationModal(false)}
            onConfirm={async (quotationId: string, notes?: string) => {
              setQuotationActionLoading(true);
              try {
                await apiService.approveCFOQuotation(currentRequest.id, { quotationId, notes });
                toast.success('Cotação aprovada com sucesso!', {
                  duration: 3000,
                  position: 'top-right',
                });
                setShowApproveQuotationModal(false);
                await loadRequestData(currentRequest.id);
              } finally {
                setQuotationActionLoading(false);
              }
            }}
            quotations={(currentRequest as MaintenanceRequest).quotations || []}
            loading={quotationActionLoading}
          />
        )}

        {/* Submit for CFO Approval Modal (issue #55) */}
        {currentRequest && isRequest(currentRequest) && (currentRequest as MaintenanceRequest).subcategory === 'compras' && (
          <SubmitForApprovalModal
            isOpen={showSubmitForApprovalModal}
            onClose={() => setShowSubmitForApprovalModal(false)}
            onConfirm={async () => {
              setQuotationActionLoading(true);
              try {
                await apiService.submitForCfoApproval(currentRequest.id);
                toast.success('Submetido para aprovação do CFO. Email enviado.', {
                  duration: 3000,
                  position: 'top-right',
                });
                setShowSubmitForApprovalModal(false);
                await loadRequestData(currentRequest.id);
              } catch (err: unknown) {
                const apiErr = err as { error?: string; message?: string };
                const message =
                  apiErr?.error ||
                  apiErr?.message ||
                  (err instanceof Error ? err.message : 'Erro ao submeter para aprovação.');
                throw new Error(message);
              } finally {
                setQuotationActionLoading(false);
              }
            }}
            quotations={(currentRequest as MaintenanceRequest).quotations || []}
            loading={quotationActionLoading}
          />
        )}

      </div>
    </div>
  );
}